import React, { useState, useEffect, Suspense, lazy, useMemo, useCallback } from 'react';
import axios from 'axios';
import './App.css';

// Lazy load heavy components for better performance
const QuestionsManagement = lazy(() => import('./components/QuestionsManagement'));
const PolicyManagement = lazy(() => import('./components/PolicyManagement'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const SignupPage = lazy(() => import('./components/SignupPage'));
const DetailedCriterion = lazy(() => import('./components/DetailedCriterion'));
const PricingComparison = lazy(() => import('./components/PricingComparison'));
const CriteriaList = lazy(() => import('./components/CriteriaList'));

// Set up axios defaults - use relative URLs for development
axios.defaults.baseURL = '';
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Define pricing packages at the top level so they can be used throughout the app
const getMonthlyPackages = () => ({
  diy: {
    name: 'DIY Toolkit',
    price: 199,
    period: 'month',
    maxUsers: 10,
    consultations: 0,
    hook: 'Self-service SOC 2 preparation tools and guidance',
    idealFor: 'Solo founders and very small teams (1-10 people) who need compliance tools without executive overhead',
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Self-serve questionnaire and policy generator',
      'Slack or email support with 24-hour response time',
      'Quarterly readiness report that flags missing controls',
      'Up to 10 team members included',
      'Tools-focused approach for self-directed teams'
    ],
    guarantee: '30-day "love-it-or-leave-it" refund guarantee',
    successGuarantee: 'With 12-month effort and payments: Additional prep coaching at no charge if initial audit fails',
    popular: false
  },
  guided: {
    name: 'Guided Essentials',
    price: 499,
    period: 'month',
    maxUsers: 50,
    consultations: 1,
    hook: 'Expert guidance and support for your SOC 2 compliance journey',
    idealFor: 'Seed-stage companies (11-50 people) needing part-time compliance leadership',
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in DIY Toolkit',
      'One 60-minute monthly call with fractional compliance officer',
      'Action-item tracker with progress and deadlines',
      'Introductions to vetted auditors',
      'Up to 50 team members included',
      'Strategic compliance guidance without full-time executive cost'
    ],
    guarantee: 'If you follow our plan and don\'t pass Type I within 4 months, we coach for free until you do',
    successGuarantee: 'With 12-month loyalty: Additional prep coaching at no charge if initial audit fails + we pay re-audit fees up to $2,500',
    popular: true
  },
  copilot: {
    name: 'Co-Pilot Plus',
    price: 999,
    period: 'month',
    maxUsers: 100,
    consultations: 2,
    hook: 'Comprehensive security leadership and weekly expert guidance',
    idealFor: 'Funded start-ups needing dedicated security leadership for SOC 2 Type II',
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in Guided Essentials',
      'Bi-Weekly 30-minute calls with fractional information security officer',
      'Automated evidence collection via GitHub, AWS, and Google Workspace integrations',
      'Security awareness slide deck for internal training',
      'Up to 100 team members included',
      'Acting security officer for your compliance program'
    ],
    guarantee: 'Follow every checklist item and if you still fail the audit, we pay the re-audit fee up to $4,000',
    successGuarantee: 'With 12-month loyalty: Unlimited additional prep + we pay re-audit fees up to $5,000',
    popular: false
  },
  elite: {
    name: 'Fractional CISO Elite',
    price: 2500,
    period: 'month',
    maxUsers: 200,
    consultations: 2,
    hook: 'Executive-level security leadership and strategic guidance',
    idealFor: 'Scale-ups and agencies (50-200 people) that need an acting CISO',
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in Co-Pilot Plus',
      'Dedicated fractional CISO time for strategy, vendor reviews, and risk assessments',
      'Customized policies that map to SOC 2 plus additional frameworks (ISO 27001, HIPAA, etc.)',
      'Annual internal audit support and penetration-test coordination',
      'Quarterly board-level security reporting pack',
      'Up to 200 team members included',
      'Full fractional CISO services and executive presence'
    ],
    guarantee: 'Same re-audit protection as Co-Pilot Plus, plus 10% fee rebate if milestones slip because of our side',
    successGuarantee: 'With 12-month effort and payments: Unlimited additional prep + we pay re-audit fees up to $10,000 + 15% fee rebate if timeline delays are our fault',
    minimumTerm: '12-months, to use guarantee',
    popular: false
  }
});

const getYearlyPackages = () => ({
  starter: {
    name: 'Starter Annual',
    price: 2500,
    period: 'year',
    maxUsers: 25,
    consultations: 12,
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Complete SOC 2 readiness assessment',
      'Comprehensive gap analysis',
      'Policy and procedure templates',
      'Evidence collection guidance',
      'Team collaboration tools',
      'Progress tracking dashboard',
      'Dedicated account manager',
      'SOC 2 inventory management',
      'Security awareness training',
      'Risk assessment exercises',
      'Virtual CISO services',
      '12 fractional compliance officer consultation sessions (1 per month)',
      'Up to 25 team members included',
      'Email support with 24-hour response'
    ],
    guarantee: '90-day money-back guarantee for new customers',
    successGuarantee: 'If you follow our plan and don\'t pass your first audit, we provide additional prep coaching at no charge',
    popular: false
  },
  professional: {
    name: 'Professional Annual',
    price: 5000,
    period: 'year',
    maxUsers: 100,
    consultations: 24,
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in Starter Annual',
      'Priority email & phone support',
      'Enhanced virtual CISO services',
      'Advanced risk exercises',
      'Custom training programs',
      '24 fractional information security officer consultation sessions (2 per month)',
      'Up to 100 users may use our system',
      'Quarterly check-ins and readiness reports',
      'Part-time compliance officer guidance'
    ],
    guarantee: '90-day money-back guarantee for new customers',
    successGuarantee: 'Pass guarantee: Additional prep coaching at no charge if initial audit fails + we pay re-audit fees up to $5,000',
    popular: true
  },
  enterprise: {
    name: 'Enterprise Annual',
    price: 12000,
    period: 'year',
    maxUsers: 200,
    consultations: 24,
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in Professional Annual',
      'Dedicated fractional compliance manager',
      'Custom policy development',
      'Advanced reporting & analytics',
      'Executive-level virtual CISO',
      'Comprehensive risk exercise programs',
      'Custom security training development',
      '24 fractional CISO consultation sessions (2 per month)',
      'Up to 200 users may use our system',
      '24/7 priority support'
    ],
    guarantee: '90-day money-back guarantee for new customers',
    successGuarantee: 'Complete pass guarantee: Unlimited additional prep + we pay re-audit fees up to $5,000',
    popular: false
  },
  premium: {
    name: 'Premium Plus Annual',
    price: 25000,
    period: 'year',
    maxUsers: 400,
    consultations: 24,
    features: [
      'Access to our SOC 2 System with compliance framework, control customization, and continuous monitoring',
      'AI-powered control, test, and risk mapping',
      'Asset management, personnel management, and risk management tools',
      'Access reviews, vendor inventory, and advanced Trust Center',
      'Questionnaire automation and customized tests with API-based automation',
      'Role-based access controls and SSO',
      'Everything in Enterprise Annual',
      'Continuous compliance monitoring',
      'Executive briefings with fractional CISO',
      'Board-level virtual CISO reporting',
      'Enterprise risk management programs',
      'Executive security training',
      '24 dedicated fractional CISO sessions (2 per month)',
      'Up to 400 users',
      'Dedicated success team',
      'Quarterly compliance reviews with C-level reporting'
    ],
    guarantee: '90-day money-back guarantee for new customers',
    successGuarantee: 'Complete pass guarantee: Unlimited additional prep + we pay re-audit fees up to $5,000 + 15% fee rebate if timeline delays are our fault',
    popular: false
  }
});



const SignupModal = ({ isOpen, onClose, selectedPlan, billingPeriod }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    position: '',
    companyAddress: '',
    companyDomain: '',
    companyDescription: '',
    mainServices: '',
    estimatedRevenue: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const packages = billingPeriod === 'monthly' ? getMonthlyPackages() : getYearlyPackages();
  const selectedPkg = packages[selectedPlan];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSignupData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveStepData = async (stepData) => {
    try {
      // Save to localStorage as backup
      localStorage.setItem(`signupData_step${currentStep}`, JSON.stringify(stepData));
      
      // Save to server
      const response = await axios.post('/api/signup/save-step', {
        step_number: currentStep,
        step_data: stepData,
        session_id: sessionId
      });
      
      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
      }
      
      return true;
    } catch (error) {
      console.error('Error saving step data:', error);
      setError('Failed to save data. Please try again.');
      return false;
    }
  };

  const handleNextStep = async () => {
    setLoading(true);
    setError('');
    
    if (currentStep === 1) {
      // Validate step 1 required fields
      if (!signupData.firstName || !signupData.lastName || !signupData.email || !signupData.phoneNumber || !signupData.companyName) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }
      const success = await saveStepData({
        firstName: signupData.firstName,
        lastName: signupData.lastName,
        email: signupData.email,
        phoneNumber: signupData.phoneNumber,
        companyName: signupData.companyName
      });
      if (success) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      // Validate step 2 required fields
      if (!signupData.position || !signupData.companyAddress) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }
      const success = await saveStepData({
        position: signupData.position,
        companyAddress: signupData.companyAddress
      });
      if (success) {
        setCurrentStep(3);
      }
    }
    setLoading(false);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
    setError('');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate step 3 required fields
    if (!signupData.companyDomain || !signupData.companyDescription || !signupData.mainServices || !signupData.estimatedRevenue) {
      setError('Please fill in all required fields');
      setLoading(false);
      return;
    }

    try {
      // Save final step data
      const success = await saveStepData({
        companyDomain: signupData.companyDomain,
        companyDescription: signupData.companyDescription,
        mainServices: signupData.mainServices,
        estimatedRevenue: signupData.estimatedRevenue
      });
      
      if (!success) {
        setLoading(false);
        return;
      }

      // Track sign-up form submission
      if (window.gtag) {
        window.gtag('event', 'sign_up', {
          method: 'form',
          package: selectedPkg.name,
          billing_period: billingPeriod,
          value: selectedPkg.price
        });
      }

      // Create Stripe checkout session
      const response = await axios.post('/api/stripe/create-checkout-session', {
        customer_data: signupData,
        package_info: {
          package_id: selectedPlan,
          package_name: selectedPkg.name,
          price: selectedPkg.price,
          billing_period: billingPeriod,
          max_users: selectedPkg.maxUsers,
          consultations: selectedPkg.consultations || 0
        },
        session_id: sessionId,
        success_url: `${window.location.origin}/payment-success`,
        cancel_url: `${window.location.origin}/payment-cancelled`
      });

      if (response.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process payment. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="signup-modal">
        <button className="modal-close" onClick={onClose}>×</button>
        
        <div className="modal-header">
          <h3>Complete Your Sign-Up</h3>
          <div className="selected-package-summary">
            <h4>Selected Package: {selectedPkg.name}</h4>
            <p>Total: ${selectedPkg.price.toLocaleString()}/{selectedPkg.period}</p>
          </div>
          
          <div className="step-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
          </div>
        </div>

        <form onSubmit={handleFormSubmit} className="signup-modal-form">
          {error && <div className="error-message">{error}</div>}

          {currentStep === 1 && (
            <div className="step-content">
              <h4>Personal & Company Information</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name: *</label>
                  <input 
                    type="text" 
                    name="firstName"
                    value={signupData.firstName}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label>Last Name: *</label>
                  <input 
                    type="text" 
                    name="lastName"
                    value={signupData.lastName}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Email: *</label>
                <input 
                  type="email" 
                  name="email"
                  value={signupData.email}
                  onChange={handleInputChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Phone Number: *</label>
                <input 
                  type="tel" 
                  name="phoneNumber"
                  value={signupData.phoneNumber}
                  onChange={handleInputChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label>Company Name: *</label>
                <input 
                  type="text" 
                  name="companyName"
                  value={signupData.companyName}
                  onChange={handleInputChange}
                  required 
                />
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="step-content">
              <h4>Professional Information</h4>
              <div className="form-group">
                <label>Position at Company: *</label>
                <select 
                  name="position"
                  value={signupData.position}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select your role...</option>
                  <option value="ciso">CISO / Security Officer</option>
                  <option value="cto">CTO / Technology Leader</option>
                  <option value="compliance">Compliance Manager</option>
                  <option value="ceo">CEO / Executive</option>
                  <option value="operations">Operations Manager</option>
                  <option value="legal">Legal / Risk Manager</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Company Address: *</label>
                <textarea 
                  name="companyAddress"
                  value={signupData.companyAddress}
                  onChange={handleInputChange}
                  required
                  rows="3"
                ></textarea>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="step-content">
              <h4>Company Details</h4>
              <div className="form-group">
                <label>Company Domain: *</label>
                <input 
                  type="url" 
                  name="companyDomain"
                  value={signupData.companyDomain}
                  onChange={handleInputChange}
                  placeholder="https://yourcompany.com" 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Company Description: *</label>
                <textarea 
                  name="companyDescription"
                  value={signupData.companyDescription}
                  onChange={handleInputChange}
                  placeholder="Brief description of your company and what you do..." 
                  required
                  rows="3"
                ></textarea>
              </div>
              <div className="form-group">
                <label>Main Services or Products: *</label>
                <textarea 
                  name="mainServices"
                  value={signupData.mainServices}
                  onChange={handleInputChange}
                  placeholder="Describe your main offerings..." 
                  required
                  rows="3"
                ></textarea>
              </div>
              <div className="form-group">
                <label>Estimated Yearly Revenue: *</label>
                <select 
                  name="estimatedRevenue"
                  value={signupData.estimatedRevenue}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select revenue range...</option>
                  <option value="under1m">Under $1M</option>
                  <option value="1m-5m">$1M - $5M</option>
                  <option value="5m-10m">$5M - $10M</option>
                  <option value="10m-25m">$10M - $25M</option>
                  <option value="25m-50m">$25M - $50M</option>
                  <option value="50m-100m">$50M - $100M</option>
                  <option value="over100m">Over $100M</option>
                </select>
              </div>
            </div>
          )}

          <div className="step-navigation">
            {currentStep > 1 && (
              <button type="button" className="prev-btn" onClick={handlePrevStep}>
                Previous
              </button>
            )}
            {currentStep < 3 ? (
              <button type="button" className="next-btn" onClick={handleNextStep} disabled={loading}>
                {loading ? 'Saving...' : 'Next'}
              </button>
            ) : (
              <button type="submit" className="signup-submit-btn" disabled={loading}>
                {loading ? 'Processing...' : `Continue to Payment ($${selectedPkg.price.toLocaleString()}/${selectedPkg.period})`}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// SignupPage component moved to separate file and imported as lazy component at top

const SignupForm = () => {
  return null; // This component is no longer used directly
};

const PasswordResetForm = ({ token, onClose }) => {
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    // Verify token validity when component mounts
    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
        setTokenValid(response.data.valid);
        if (!response.data.valid) {
          setError('This password reset link is invalid or has expired.');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Unable to verify reset token. Please try again.');
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await axios.post('/api/auth/reset-password', {
        token: token,
        new_password: formData.password
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (tokenValid === false) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Invalid Reset Link</h2>
          <div className="error-message">{error}</div>
          <p>This password reset link is invalid or has expired. Please request a new password reset.</p>
          <button onClick={onClose} className="primary-btn">
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Verifying Reset Link...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Password Reset Successful</h2>
          <div className="success-message">
            Your password has been successfully reset. You can now log in with your new password.
          </div>
          <button onClick={onClose} className="primary-btn">
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Reset Your Password</h2>
        
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password:</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="8"
              placeholder="Enter your new password"
            />
            <small>Password must be at least 8 characters long</small>
          </div>

          <div className="form-group">
            <label>Confirm New Password:</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="Confirm your new password"
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <p>
          <button 
            type="button" 
            className="link-button"
            onClick={onClose}
          >
            Back to Login
          </button>
        </p>
      </div>
    </div>
  );
};

const LoginForm = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/api/auth/login', {
        email: formData.email,
        password: formData.password
      });

      // Store token and user info
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      onLogin(response.data.user, false);
    } catch (err) {
      console.error('Login error details:', err);
      const errorMessage = err.response?.status === 500 
        ? 'Server error during login. Please check if the server is running properly.' 
        : err.response?.data?.detail || 'Authentication failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResetMessage('');

    try {
      await axios.post('/api/auth/request-password-reset', {
        email: resetEmail
      });
      setResetMessage('Password reset instructions have been sent to your email.');
    } catch (err) {
      setResetMessage(err.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSOC2PrepClick = () => {
    // Close the login modal and navigate to SOC 2 prep service
    onLogin(null, false); // Close modal without logging in
    // The parent component should handle navigation to soc2prep tab
    setTimeout(() => {
      const mainTabButton = document.querySelector('[data-main-tab="soc2prep"]');
      if (mainTabButton) {
        mainTabButton.click();
      }
    }, 100);
  };

  if (showPasswordReset) {
    return (
      <div className="auth-container">
        <div className="auth-form">
          <h2>Password Reset</h2>

          {resetMessage && (
            <div className={`message ${resetMessage.includes('sent') ? 'success' : 'error'}`}>
              {resetMessage}
            </div>
          )}

          <form onSubmit={handlePasswordReset}>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>

          <p>
            <button 
              type="button" 
              className="link-button"
              onClick={() => setShowPasswordReset(false)}
            >
              Back to Login
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h2>Login</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password:</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Please wait...' : 'Login'}
          </button>
        </form>

        <p>
          <button 
            type="button" 
            className="link-button"
            onClick={() => setShowPasswordReset(true)}
          >
            Forgot Password? Password reset.
          </button>
        </p>

        <p>
          Wondering how to get an account?{' '}
          <button 
            type="button" 
            className="link-button"
            onClick={handleSOC2PrepClick}
          >
            Sign up for our SOC 2 PREP SERVICE
          </button>
        </p>
      </div>
    </div>
  );
};

// DetailedCriterion component is imported as lazy component at top



const PaymentSuccess = ({ onLogin }) => {
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    company_name: ''
  });
  const [registering, setRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState('');

  useEffect(() => {
    // Get session ID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      // Fetch actual session details from backend
      fetchSessionDetails(sessionId);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await axios.get(`/api/stripe/session/${sessionId}`);
      if (response.data) {
        const data = {
          sessionId,
          amount: `$${(response.data.amount_total / 100).toLocaleString()}`,
          packageName: response.data.package_name || 'Professional',
          billing_period: response.data.billing_period || 'year',
          customerEmail: response.data.customer_email,
          userExists: response.data.user_exists
        };
        
        setSessionData(data);

        // If user doesn't exist, show registration form
        if (!response.data.user_exists && response.data.customer_email) {
          setShowRegistration(true);
          setRegistrationData(prev => ({
            ...prev,
            email: response.data.customer_email
          }));
        }

        // Track payment success with actual values
        if (window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: sessionId,
            value: response.data.amount_total / 100,
            currency: 'USD',
            items: [{
              item_id: response.data.package_id || 'professional_package',
              item_name: `${response.data.package_name || 'Professional'} SOC 2 Package`,
              category: 'subscription',
              quantity: 1,
              price: response.data.amount_total / 100
            }]
          });
        }
      }
    } catch (error) {
      console.error('Error fetching session details:', error);
      // Fallback to default display
      setSessionData({
        sessionId,
        amount: 'Payment Amount',
        packageName: 'SOC 2 Package'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setRegistering(true);
    setRegistrationError('');

    // Validate passwords match
    if (registrationData.password !== registrationData.confirmPassword) {
      setRegistrationError('Passwords do not match');
      setRegistering(false);
      return;
    }

    // Validate password strength
    if (registrationData.password.length < 8) {
      setRegistrationError('Password must be at least 8 characters long');
      setRegistering(false);
      return;
    }

    try {
      const response = await axios.post(`/api/auth/register-after-payment?session_id=${sessionData.sessionId}`, {
        email: registrationData.email,
        password: registrationData.password,
        first_name: registrationData.first_name,
        last_name: registrationData.last_name,
        company_name: registrationData.company_name
      });

      // Store token and user info
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

      // Call the login handler to update app state
      if (onLogin) {
        onLogin(response.data.user, true);
      }

      // Redirect to dashboard
      window.location.href = '/#soc2prep';
    } catch (error) {
      setRegistrationError(error.response?.data?.detail || 'Registration failed. Please try again.');
      setRegistering(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading payment details...</div>;
  }

  return (
    <div className="payment-result success">
      <div className="result-content">
        <div className="success-icon">✅</div>
        <h2>Payment Successful!</h2>
        <p>Thank you for your purchase. Your SOC 2 preparation service is now active.</p>

        {sessionData && (
          <div className="payment-details">
            <h4>Payment Details</h4>
            <p><strong>Package:</strong> {sessionData.packageName}</p>
            <p><strong>Amount:</strong> {sessionData.amount}{sessionData.billing_period ? `/${sessionData.billing_period}` : ''}</p>
            <p><strong>Session ID:</strong> {sessionData.sessionId}</p>
          </div>
        )}

        {showRegistration && (
          <div className="registration-section">
            <h3>Create Your Account</h3>
            <p>Set up your username and password to access your SOC 2 dashboard immediately:</p>
            
            <form onSubmit={handleRegistrationSubmit} className="registration-form">
              {registrationError && (
                <div className="error-message">{registrationError}</div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>First Name:</label>
                  <input
                    type="text"
                    name="first_name"
                    value={registrationData.first_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name:</label>
                  <input
                    type="text"
                    name="last_name"
                    value={registrationData.last_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  name="email"
                  value={registrationData.email}
                  onChange={handleInputChange}
                  required
                  disabled
                />
              </div>

              <div className="form-group">
                <label>Company Name:</label>
                <input
                  type="text"
                  name="company_name"
                  value={registrationData.company_name}
                  onChange={handleInputChange}
                  placeholder="Leave blank to use company name from signup"
                />
              </div>

              <div className="form-group">
                <label>Password:</label>
                <input
                  type="password"
                  name="password"
                  value={registrationData.password}
                  onChange={handleInputChange}
                  required
                  minLength="8"
                />
                <small>At least 8 characters</small>
              </div>

              <div className="form-group">
                <label>Confirm Password:</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={registrationData.confirmPassword}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <button type="submit" className="primary-btn" disabled={registering}>
                {registering ? 'Creating Account...' : 'Create Account & Access Dashboard'}
              </button>
            </form>
          </div>
        )}

        {!showRegistration && (
          <div className="next-steps">
            <h4>What's Next?</h4>
            <ol>
              <li>Check your email for a confirmation receipt</li>
              <li>{sessionData?.userExists ? 'Log in with your existing account' : 'Check your email for login credentials'}</li>
              <li>Access your SOC 2 preparation dashboard</li>
              <li>Schedule your first expert consultation</li>
            </ol>
          </div>
        )}

        <button 
          className="secondary-btn"
          onClick={() => window.location.href = '/'}
        >
          Return to Home
        </button>
      </div>
    </div>
  );
};

const ConsultationForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Track consultation form submission
      if (window.gtag) {
        window.gtag('event', 'generate_lead', {
          form_type: 'consultation',
          method: 'form'
        });
      }

      // Send form data to backend for email notifications
      const response = await fetch('/api/contact/consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });

      if (response.ok) {
        // Open Calendly after successful form submission
        window.open('https://calendly.com/casejones/meeting', '_blank');
        alert('Thank you! Your consultation request has been submitted. Please check your email for confirmation.');
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting consultation form:', error);
      alert('There was an error submitting your request. Please try again.');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="consultation-form">
      <h3>Schedule Your Consultation</h3>
      <p>Please provide your contact information so we can better assist you during the consultation.</p>

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Full Name:</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Email:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Phone Number:</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Message (Optional):</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your SOC 2 needs or any specific questions you have..."
            rows="4"
          />
        </div>

        <div className="form-actions">
          <button type="submit" disabled={loading} className="primary-btn">
            {loading ? 'Processing...' : 'Continue to Calendly'}
          </button>
          <button type="button" onClick={onClose} className="secondary-btn">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

const PaymentCancelled = () => {
  return (
    <div className="payment-result cancelled">
      <div className="result-content">
        <div className="cancel-icon">❌</div>
        <h2>Payment Cancelled</h2>
        <p>Your payment was cancelled. No charges have been made to your account.</p>

        <div className="help-section">
          <h4>Need Help?</h4>
          <p>If you encountered any issues during checkout, please contact our support team:</p>
          <ul>
            <li>Email: support@soc2experts.com</li>
            <li>Phone: +1 (415) 906-6005</li>
          </ul>
        </div>

        <div className="action-buttons">
          <button 
            className="primary-btn"
            onClick={() => window.location.href = '/#soc2prep/signup'}
          >
            Try Again
          </button>
          <button 
            className="secondary-btn"
            onClick={() => window.location.href = '/'}
          >
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

// Large components moved to separate files for better performance

const App = () => {
  const [user, setUser] = useState(null);
  const [criteria, setCriteria] = useState([]);
  const [activeTab, setActiveTab] = useState('CC');
  const [activeMainTab, setActiveMainTab] = useState('soc2prep');
  const [activePrepTab, setActivePrepTab] = useState('description');
  const [activeDashboardTab, setActiveDashboardTab] = useState('dashboard');
  const [activeAboutTab, setActiveAboutTab] = useState('founder');
  const [activeGeneralTab, setActiveGeneralTab] = useState('dashboard');
  const [activeTrustCriteriaTab, setActiveTrustCriteriaTab] = useState('policies');
  const [activeCustomerTab, setActiveCustomerTab] = useState('values');
  const [activeFaqTab, setActiveFaqTab] = useState('presoc2costs');
  const [activeContactTab, setActiveContactTab] = useState('consultations');
  const [activePricingBilling, setActivePricingBilling] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Loading SOC 2 Criteria...');
  const [showLogin, setShowLogin] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [showFeatureComparison, setShowFeatureComparison] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    logo_url: '',
    head_office_address: '',
    services_description: '',
    industry: ''
  });
  const [isSavingBusinessInfo, setIsSavingBusinessInfo] = useState(false);
  const [businessInfoMessage, setBusinessInfoMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [evidenceTab, setEvidenceTab] = useState('all');
  const [uploadingEvidence, setUploadingEvidence] = useState({});
  const [questions, setQuestions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    policies: { completed: 0, total: 0 },
    questions: { completed: 0, total: 0 },
    evidence: { completed: 0, total: 0 },
    readinessScore: 0
  });

  // Data caching for improved UI responsiveness
  const [cachedData, setCachedData] = useState({
    policies: null,
    questions: null,
    teamMembers: null,
    evidence: null,
    businessInfo: null,
    lastUpdated: {}
  });

  const [dataLoading, setDataLoading] = useState({
    policies: false,
    questions: false,
    teamMembers: false,
    evidence: false,
    businessInfo: false
  });

  // Jokes state for loading screen - moved to top with other hooks
  const [currentJoke, setCurrentJoke] = useState(0);
  const soc2Jokes = [
    "Why did the auditor break up with the SOC 2 report?<br>Because they had too many control issues.",
    "Why do SOC 2 audits never go to parties?<br>Because they always find gaps in the fun.",
    "How many auditors does it take to change a lightbulb?<br>Three. One to change it, one to document it, and one to verify the first two.",
    "Why did the database apply for SOC 2?<br>It wanted to prove it had commitment and integrity.",
    "Why was the firewall invited to the SOC 2 kickoff?<br>Because it knows how to set boundaries.",
    "Why don't SOC 2 reports tell jokes?<br>They're too serious about control objectives.",
    "Why did the auditor bring two socks to the interview?<br>Separation of duties."
  ];

  // Check for payment result pages and password reset
  const currentPath = window.location.pathname;
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');
  const isPasswordResetPage = currentPath === '/reset-password' && resetToken;

  // Different navigation for logged-in vs non-logged-in users
  const mainTabsLoggedOut = [
    { code: 'soc2prep', name: 'SOC 2 Prep Service', description: 'Service to prepare for SOC 2 audit' },
    { code: 'customer', name: 'Our Ideal Customer', description: 'The type of customer we best serve and support' },
    { code: 'about', name: 'About Us', description: 'Learn more about our SOC 2 expertise and services' },
    { code: 'faq', name: 'FAQ', description: 'Frequently asked questions about SOC 2 and our services' },
    { code: 'contact', name: 'Contact Us', description: 'Get in touch with our SOC 2 compliance experts' }
  ];

  const mainTabsLoggedIn = [
    { code: 'general', name: 'General', description: 'Dashboard, business info, team, policies, and billing' },
    { code: 'security', name: 'Security', description: 'Security controls, policies, questions, and evidence' },
    { code: 'availability', name: 'Availability', description: 'Availability controls, policies, questions, and evidence' },
    { code: 'processing', name: 'Processing Integrity', description: 'Processing integrity controls, policies, questions, and evidence' },
    { code: 'confidentiality', name: 'Confidentiality', description: 'Confidentiality controls, policies, questions, and evidence' },
    { code: 'privacy', name: 'Privacy', description: 'Privacy controls, policies, questions, and evidence' },
    { code: 'faq', name: 'FAQ', description: 'Frequently asked questions about SOC 2 and our services' },
    { code: 'contact', name: 'Contact Us', description: 'Get in touch with our SOC 2 compliance experts' }
  ];

  const generalTabs = [
    { code: 'dashboard', name: 'Dashboard', description: 'Audit readiness score and progress overview' },
    { code: 'business', name: 'Business', description: 'Company information and business details' },
    { code: 'team', name: 'Team', description: 'Manage team members and access' },
    { code: 'policies', name: 'Policies', description: 'Upload and manage company policies' },
    { code: 'billing', name: 'Billing', description: 'Manage payment methods and invoices' }
  ];

  const trustCriteriaTabs = [
    { code: 'policies', name: 'Policies', description: 'Policies relevant to this trust criterion' },
    { code: 'questions', name: 'Questions', description: 'Assessment questions for this trust criterion' },
    { code: 'evidence', name: 'Evidence', description: 'Evidence collection for this trust criterion' }
  ];

  const aboutTabs = [
    { code: 'founder', name: 'Who We Are - Founder', description: 'Meet our founder and leadership' },
    { code: 'mission', name: 'Our Mission, Approach & Values', description: 'Our mission, approach, and core values' },
    { code: 'whychoose', name: 'Why Choose Us', description: 'Competitive analysis and what sets us apart' }
  ];

  const customerTabs = [
    { code: 'values', name: 'Values', description: 'The values our ideal customers embody' },
    { code: 'characteristics', name: 'Characteristics', description: 'Key characteristics of companies we best serve' },
    { code: 'commitment', name: 'Commitment', description: 'What commitment means to our customers' }
  ];

  const faqTabs = [
    { code: 'presoc2costs', name: 'Pre-SOC 2 Costs', description: 'The hidden costs of operating without a compliance framework' },
    { code: 'soc2criteria', name: 'SOC 2 Trust Criteria', description: 'If you haven\'t taken a look at Pre-SOC 2 Costs, go there first. Here is a detailed description of what SOC 2 actually requires.' },
    { code: 'type1audit', name: 'SOC 2 Type I Audit', description: 'Understanding SOC 2 Type I audit process and requirements' },
    { code: 'type2audit', name: 'SOC 2 Type II Audit', description: 'Understanding SOC 2 Type II audit process and requirements' },
    { code: 'general', name: 'FAQ', description: 'General frequently asked questions about our services' },
    { code: 'budgeting', name: 'SOC 2 Budgeting', description: 'Detailed cost breakdowns for SOC 2 preparation scenarios' }
  ];

  const contactTabs = [
    { code: 'consultations', name: 'Consultations', description: 'Schedule consultations and get in touch with our experts' },
    { code: 'careers', name: 'Careers', description: 'Join our team and help businesses achieve SOC 2 compliance' },
    { code: 'partnerships', name: 'Partnerships', description: 'Partner with us to deliver SOC 2 services to your clients' }
  ];

  const criteriaTabs = [
    { code: 'CC', name: 'Security (Common Criteria)', description: 'Information and systems are protected against unauthorized access' },
    { code: 'A', name: 'Availability', description: 'Information or systems are available for operation and use as committed' },
    { code: 'PI', name: 'Processing Integrity', description: 'System processing is complete, valid, accurate, timely, and authorized' },
    { code: 'C', name: 'Confidentiality', description: 'Information designated as confidential is protected as committed' },
    { code: 'P', name: 'Privacy', description: 'Personal information is collected, used, retained, disclosed, and disposed of in conformity with commitments' }
  ];

  const prepServiceTabs = [
    { code: 'description', name: 'Service Description', description: 'Comprehensive SOC 2 audit preparation service' },
    { code: 'privacy', name: 'Privacy Policy', description: 'How we protect and handle your data' },
    { code: 'terms', name: 'Terms of Service', description: 'Terms and conditions for using our service' },
    { code: 'signup', name: 'Pricing & Sign-Up', description: 'Choose your package and get started with your SOC 2 preparation journey' }
  ];

  const prepDashboardTabs = [
    { code: 'dashboard', name: 'Dashboard', description: 'Audit readiness score and progress overview' },
    { code: 'business', name: 'Business', description: 'Company information and business details' },
    { code: 'team', name: 'Team', description: 'Manage team members and access' },
    { code: 'policies', name: 'Policies', description: 'Upload and manage company policies' },
    { code: 'questions', name: 'Questions', description: 'SOC 2 assessment questionnaire' },
    { code: 'evidence', name: 'Evidence', description: 'Evidence collection for SOC 2 compliance' },
    { code: 'billing', name: 'Billing', description: 'Manage payment methods and invoices' }
  ];

  useEffect(() => {
    // Hide the initial HTML loader with multiple removal methods
    const removeInitialLoader = () => {
      const initialLoader = document.getElementById('initial-loader');
      if (initialLoader) {
        // Try multiple removal methods
        initialLoader.style.display = 'none';
        initialLoader.style.visibility = 'hidden';
        initialLoader.style.opacity = '0';
        // Remove it completely after a brief delay
        setTimeout(() => {
          if (initialLoader && initialLoader.parentNode) {
            initialLoader.parentNode.removeChild(initialLoader);
          }
        }, 100);
      }
    };

    // Add chunk load error handlers
    const handleChunkError = (event) => {
      if (event.message && event.message.includes('Loading chunk') && event.message.includes('failed')) {
        console.error('Chunk load error in React app:', event.message);
        // Don't auto-reload here since the HTML page handler will take care of it
        return;
      }
    };

    const handlePromiseRejection = (event) => {
      if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
        console.error('Chunk load promise rejection in React app:', event.reason.message);
        event.preventDefault();
        return;
      }
    };

    // Add error listeners
    window.addEventListener('error', handleChunkError);
    window.addEventListener('unhandledrejection', handlePromiseRejection);

    // Remove immediately and also after multiple intervals as backup
    removeInitialLoader();
    setTimeout(removeInitialLoader, 50);
    setTimeout(removeInitialLoader, 200);
    setTimeout(removeInitialLoader, 500);

    // Track page view
    if (window.gtag) {
      window.gtag('config', 'AW-16994144454', {
        page_title: 'SOC 2 Experts .com',
        page_location: window.location.href
      });
    }

    // Ensure the React app is visible
    const ensureAppVisibility = () => {
      const appElement = document.querySelector('.app');
      if (appElement) {
        appElement.style.visibility = 'visible';
        appElement.style.opacity = '1';
      }
    };

    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Set logged-in users to start on General tab
        setActiveMainTab('general');
        setActiveGeneralTab('dashboard');
      } catch (err) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }

    // Always load criteria (whether authenticated or not)
    fetchCriteria();
    
    // Ensure app visibility after a short delay
    setTimeout(ensureAppVisibility, 100);

    // Cleanup function
    return () => {
      window.removeEventListener('error', handleChunkError);
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
    };
  }, []);

  // Cycle through jokes while loading
  useEffect(() => {
    if (loading) {
      const jokeInterval = setInterval(() => {
        setCurrentJoke(prev => (prev + 1) % soc2Jokes.length);
      }, 3500);

      return () => clearInterval(jokeInterval);
    }
  }, [loading, soc2Jokes.length]);

  const fetchCriteria = async (retryCount = 0) => {
    try {
      const response = await axios.get('/api/criteria', {
        timeout: 10000, // 10 second timeout
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      setCriteria(response.data);
      setLoading(false);
      setError(null); // Clear any previous errors
      console.log('✅ Successfully fetched criteria from server');
    } catch (err) {
      console.log(`🔄 Attempt ${retryCount + 1} to fetch criteria failed:`, err.message);
      
      if (err.response?.status === 401 && user) {
        // Token expired or invalid, only logout if user was logged in
        handleLogout();
      } else if (err.response?.status === 500) {
        // Server error - retry with exponential backoff
        if (retryCount < 5) {
          const delay = Math.min(2000 * Math.pow(1.5, retryCount), 10000); // Max 10 second delay
          setLoadingMessage(`Server error detected. Retrying ${retryCount + 1}/5 in ${Math.ceil(delay/1000)}s...`);
          console.log(`🚨 Server error (500), retrying in ${delay}ms... (attempt ${retryCount + 1}/5)`);
          setTimeout(() => fetchCriteria(retryCount + 1), delay);
          return;
        }
        setError('Server is experiencing issues. Please try refreshing the page or contact support if the problem persists.');
        setLoading(false);
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK' || err.code === 'ENOTFOUND' || !err.response) {
        // Backend not ready yet, retry with exponential backoff up to 10 times
        if (retryCount < 10) {
          const delay = Math.min(1000 * Math.pow(1.5, retryCount), 8000); // Max 8 second delay
          setLoadingMessage(`Server starting up... Retry ${retryCount + 1}/10 in ${Math.ceil(delay/1000)}s`);
          console.log(`⏳ Server not ready, retrying in ${delay}ms... (attempt ${retryCount + 1}/10)`);
          setTimeout(() => fetchCriteria(retryCount + 1), delay);
          return;
        }
        setError('Server is taking longer than expected to start. Please wait a moment and refresh the page.');
        setLoading(false);
      } else {
        setError(`Failed to fetch criteria: ${err.response?.data?.detail || err.message}`);
        setLoading(false);
      }
    }
  };

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/questions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchEvidence = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get('/api/evidence', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setEvidence(response.data);
      // Refresh dashboard stats when evidence changes
      fetchDashboardStats();
    } catch (error) {
      console.error('Error fetching evidence:', error);
    }
  };

  const initializeEvidence = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.post('/api/evidence/initialize', {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchEvidence();
    } catch (error) {
      console.error('Error initializing evidence:', error);
    }
  };

  const uploadEvidenceFile = async (evidenceId, file) => {
    try {
      setUploadingEvidence(prev => ({ ...prev, [evidenceId]: true }));
      const token = localStorage.getItem('token');
      if (!token) return;

      // Upload file metadata first
      const fileData = {
        original_filename: file.name,
        file_size: file.size,
        content_type: file.type,
        description: `Evidence file: ${file.name}`
      };

      const fileResponse = await axios.post('/api/upload-file', fileData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Attach to evidence record
      const evidenceData = {
        evidence_id: evidenceId,
        uploaded_file_id: fileResponse.data.file_id,
        notes: `Uploaded ${file.name} as evidence`
      };

      await axios.post('/api/evidence/upload', evidenceData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchEvidence();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error uploading evidence:', error);
      alert('Error uploading evidence file');
    } finally {
      setUploadingEvidence(prev => ({ ...prev, [evidenceId]: false }));
    }
  };

  const updateEvidenceStatus = async (evidenceId, status) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.put(`/api/evidence/${evidenceId}/status?status=${status}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      fetchEvidence();
      fetchDashboardStats();
    } catch (error) {
      console.error('Error updating evidence status:', error);
    }
  };

  const handleLogin = (userData, wasRegistration = false) => {
    setUser(userData);
    setShowLogin(false);
    
    // Only fetch criteria if login was successful
    if (userData) {
      fetchCriteria();
    }

    // If this was a registration, redirect to SOC 2 prep service
    if (wasRegistration) {
      setActiveMainTab('soc2prep');
      // For logged-in users, go to dashboard; for non-logged-in, go to signup
      if (userData) {
        setActiveDashboardTab('dashboard');
      } else {
        setActivePrepTab('signup');
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/api/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setCriteria([]);
      setLoading(false);
    }
  };

  const getTabCriteria = useCallback((tabCode) => {
    return criteria.filter(c => c && c.category_code === tabCode);
  }, [criteria]);

  // Memoize filtered criteria to prevent unnecessary re-filtering
  const filteredCriteria = useMemo(() => {
    return getTabCriteria(activeTab);
  }, [getTabCriteria, activeTab]);

  const getStripePublishableKey = async () => {
    try {
      const response = await axios.get('/api/stripe/publishable-key');
      return response.data.publishable_key;
    } catch (err) {
      console.error('Failed to fetch Stripe publishable key:', err);
      return null;
    }
  };

  const getCurrentMainTab = () => {
    const currentTabs = user ? mainTabsLoggedIn : mainTabsLoggedOut;
    return currentTabs.find(tab => tab.code === activeMainTab);
  };

  const getCurrentCriteriaTab = () => {
    return criteriaTabs.find(tab => tab.code === activeTab);
  };

  const getCurrentPrepTab = () => {
    return prepServiceTabs.find(tab => tab.code === activePrepTab);
  };

  const getCurrentAboutTab = () => {
    return aboutTabs.find(tab => tab.code === activeAboutTab);
  };

  const getCurrentCustomerTab = () => {
    return customerTabs.find(tab => tab.code === activeCustomerTab);
  };

  const getCurrentFaqTab = () => {
    return faqTabs.find(tab => tab.code === activeFaqTab);
  };

  const getCurrentContactTab = () => {
    return contactTabs.find(tab => tab.code === activeContactTab);
  };

  const getCurrentGeneralTab = () => {
    return generalTabs.find(tab => tab.code === activeGeneralTab);
  };

  const getCurrentTrustCriteriaTab = () => {
    return trustCriteriaTabs.find(tab => tab.code === activeTrustCriteriaTab);
  };

  const handleMainTabClick = (tabCode) => {
    setActiveMainTab(tabCode);
    if (tabCode === 'soc2prep') {
      if (user) {
        // For logged-in users, redirect to general instead
        setActiveMainTab('general');
        setActiveGeneralTab('dashboard');
        return;
      } else {
        setActivePrepTab('description'); // Default to description for non-logged in users
      }
    } else if (tabCode === 'general') {
      setActiveGeneralTab('dashboard'); // Default to dashboard when switching to general
    } else if (tabCode === 'security' || tabCode === 'availability' || tabCode === 'processing' || tabCode === 'confidentiality' || tabCode === 'privacy') {
      setActiveTrustCriteriaTab('policies'); // Default to policies when switching to trust criteria
    } else if (tabCode === 'customer') {
      setActiveCustomerTab('values'); // Default to values when switching to customer
    } else if (tabCode === 'about') {
      setActiveAboutTab('founder'); // Default to founder when switching to about
    } else if (tabCode === 'faq') {
      setActiveFaqTab('presoc2costs'); // Default to Pre-SOC 2 costs when switching to FAQ
    } else if (tabCode === 'contact') {
      setActiveContactTab('consultations'); // Default to consultations when switching to contact
    }
  };

  const handleCriteriaTabClick = (tabCode) => {
    setActiveTab(tabCode);
  };

  const handlePrepTabClick = (tabCode) => {
    setActivePrepTab(tabCode);
  };

  const handleDashboardTabClick = (tabCode) => {
    setActiveDashboardTab(tabCode);
  };

  const handleAboutTabClick = (tabCode) => {
    setActiveAboutTab(tabCode);
  };

  const handleCustomerTabClick = (tabCode) => {
    setActiveCustomerTab(tabCode);
  };

  const handleFaqTabClick = (tabCode) => {
    setActiveFaqTab(tabCode);
  };

  const handleContactTabClick = (tabCode) => {
    setActiveContactTab(tabCode);
  };

  const handleGeneralTabClick = (tabCode) => {
    setActiveGeneralTab(tabCode);
  };

  const handleTrustCriteriaTabClick = (tabCode) => {
    setActiveTrustCriteriaTab(tabCode);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const userData = await response.json();
        setUsers(userData);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchBusinessInfo = async () => {
    try {
      const response = await fetch('/api/company/business-info', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBusinessInfo({
          logo_url: data.logo_url || '',
          head_office_address: data.head_office_address || '',
          services_description: data.services_description || '',
          industry: data.industry || ''
        });
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const saveBusinessInfo = async () => {
    setIsSavingBusinessInfo(true);
    setBusinessInfoMessage('');
    try {
      const response = await fetch('/api/company/business-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(businessInfo)
      });

      if (response.ok) {
        setBusinessInfoMessage('Business information saved successfully!');
        setTimeout(() => setBusinessInfoMessage(''), 5000); // Clear message after 5 seconds
      } else {
        const errorData = await response.json();
        setBusinessInfoMessage(`Error saving business information: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error saving business info:', error);
      setBusinessInfoMessage('Error saving business information. Please try again.');
    } finally {
      setIsSavingBusinessInfo(false);
    }
  };

  const handleBusinessInfoChange = (field, value) => {
    setBusinessInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const roleData = await response.json();
        setRoles(roleData);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Use cached data if available to reduce API calls
      let policiesData = cachedData.policies || [];
      let questionsData = cachedData.questions || [];
      let evidenceData = [];

      // Only fetch if we don't have recent cached data
      const needsFresh = !cachedData.policies || !cachedData.questions || 
                        (Date.now() - (cachedData.lastUpdated.policies || 0)) > 300000;

      if (needsFresh) {
        const requests = [
          axios.get('/api/company/policies', { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get('/api/questions', { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get('/api/evidence', { headers: { 'Authorization': `Bearer ${token}` } })
        ];

        const results = await Promise.allSettled(requests);
      
        // Process results safely
        const [policiesResult, questionsResult, evidenceResult] = results;
        
        policiesData = policiesResult.status === 'fulfilled' ? policiesResult.value.data : [];
        questionsData = questionsResult.status === 'fulfilled' ? questionsResult.value.data : [];
        evidenceData = evidenceResult.status === 'fulfilled' ? evidenceResult.value.data : [];

        // Update cache with fresh data
        const now = Date.now();
        setCachedData(prev => ({
          ...prev,
          policies: policiesData,
          questions: questionsData,
          evidence: evidenceData,
          lastUpdated: {
            ...prev.lastUpdated,
            policies: now,
            questions: now,
            evidence: now
          }
        }));
      }

      // Calculate statistics
      const requiredPolicies = policiesData.filter(p => p.is_required);
      const completedPolicies = requiredPolicies.filter(p => p.company_policy_id);
      const answeredQuestions = questionsData.filter(q => q.answer);
      const collectedEvidence = evidenceData.filter(e => e.status === 'collected');

      // Calculate totals and readiness score
      const totalCompleted = completedPolicies.length + answeredQuestions.length + collectedEvidence.length;
      const totalRequired = requiredPolicies.length + questionsData.length + evidenceData.length;
      const readinessScore = totalRequired > 0 ? Math.round((totalCompleted / totalRequired) * 100) : 0;

      setDashboardStats({
        policies: { completed: completedPolicies.length, total: requiredPolicies.length },
        questions: { completed: answeredQuestions.length, total: questionsData.length },
        evidence: { completed: collectedEvidence.length, total: evidenceData.length },
        readinessScore
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  // Cached data fetching functions with request throttling
  const fetchAndCacheTeamMembers = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    // Return cached data if available and not forcing refresh
    if (!forceRefresh && cachedData.teamMembers && cachedData.lastUpdated.teamMembers) {
      const cacheAge = Date.now() - cachedData.lastUpdated.teamMembers;
      if (cacheAge < 300000) { // 5 minutes cache
        return cachedData.teamMembers;
      }
    }

    // Prevent duplicate requests
    if (dataLoading.teamMembers && !forceRefresh) {
      return cachedData.teamMembers;
    }

    try {
      setDataLoading(prev => ({ ...prev, teamMembers: true }));
      const response = await axios.get('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const teamData = response.data;
      setCachedData(prev => ({
        ...prev,
        teamMembers: teamData,
        lastUpdated: { ...prev.lastUpdated, teamMembers: Date.now() }
      }));
      
      return teamData;
    } catch (error) {
      console.error('Error fetching team members:', error);
      return cachedData.teamMembers || [];
    } finally {
      setDataLoading(prev => ({ ...prev, teamMembers: false }));
    }
  }, []);

  const fetchAndCachePolicies = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    if (!forceRefresh && cachedData.policies && cachedData.lastUpdated.policies) {
      const cacheAge = Date.now() - cachedData.lastUpdated.policies;
      if (cacheAge < 300000) { // 5 minutes cache
        return cachedData.policies;
      }
    }

    if (dataLoading.policies && !forceRefresh) {
      return cachedData.policies;
    }

    try {
      setDataLoading(prev => ({ ...prev, policies: true }));
      const response = await axios.get('/api/company/policies', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const policiesData = response.data;
      setCachedData(prev => ({
        ...prev,
        policies: policiesData,
        lastUpdated: { ...prev.lastUpdated, policies: Date.now() }
      }));
      
      return policiesData;
    } catch (error) {
      console.error('Error fetching policies:', error);
      return cachedData.policies || [];
    } finally {
      setDataLoading(prev => ({ ...prev, policies: false }));
    }
  }, []);

  const fetchAndCacheQuestions = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    if (!forceRefresh && cachedData.questions && cachedData.lastUpdated.questions) {
      const cacheAge = Date.now() - cachedData.lastUpdated.questions;
      if (cacheAge < 300000) { // 5 minutes cache
        return cachedData.questions;
      }
    }

    if (dataLoading.questions && !forceRefresh) {
      return cachedData.questions;
    }

    try {
      setDataLoading(prev => ({ ...prev, questions: true }));
      const response = await axios.get('/api/questions', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const questionsData = response.data;
      setCachedData(prev => ({
        ...prev,
        questions: questionsData,
        lastUpdated: { ...prev.lastUpdated, questions: Date.now() }
      }));
      
      return questionsData;
    } catch (error) {
      console.error('Error fetching questions:', error);
      return cachedData.questions || [];
    } finally {
      setDataLoading(prev => ({ ...prev, questions: false }));
    }
  }, []);

  const fetchAndCacheBusinessInfo = useCallback(async (forceRefresh = false) => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    if (!forceRefresh && cachedData.businessInfo && cachedData.lastUpdated.businessInfo) {
      const cacheAge = Date.now() - cachedData.lastUpdated.businessInfo;
      if (cacheAge < 600000) { // 10 minutes cache for business info
        return cachedData.businessInfo;
      }
    }

    if (dataLoading.businessInfo && !forceRefresh) {
      return cachedData.businessInfo;
    }

    try {
      setDataLoading(prev => ({ ...prev, businessInfo: true }));
      const response = await axios.get('/api/company/business-info', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const businessData = response.data;
      setCachedData(prev => ({
        ...prev,
        businessInfo: businessData,
        lastUpdated: { ...prev.lastUpdated, businessInfo: Date.now() }
      }));
      
      setBusinessInfo({
        logo_url: businessData.logo_url || '',
        head_office_address: businessData.head_office_address || '',
        services_description: businessData.services_description || '',
        industry: businessData.industry || ''
      });
      
      return businessData;
    } catch (error) {
      console.error('Error fetching business info:', error);
      return cachedData.businessInfo || {};
    } finally {
      setDataLoading(prev => ({ ...prev, businessInfo: false }));
    }
  }, []);

  // Pre-load data when user logs in - only once per session
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user && token) {
      // Verify token is properly set in axios
      if (!axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      fetchCriteria();
      fetchUsers();
      fetchRoles();
      fetchDashboardStats();
      
      // Pre-load commonly used data in background - only if not already cached
      setTimeout(() => {
        if (!cachedData.teamMembers) fetchAndCacheTeamMembers();
        if (!cachedData.policies) fetchAndCachePolicies();
        if (!cachedData.questions) fetchAndCacheQuestions();
        if (!cachedData.businessInfo) fetchAndCacheBusinessInfo();
      }, 2000); // Longer delay to prevent overwhelming
    }
  }, [user]); // Remove dependencies to prevent re-triggering


  useEffect(() => {
    const token = localStorage.getItem('token');
    if (user && token && activeDashboardTab === 'evidence') {
      fetchEvidence();
    }
  }, [user, activeDashboardTab]);


  // Handle password reset page
  if (isPasswordResetPage) {
    return (
      <div className="app">
        <header className="header">
          <div className="header-content">
            <div className="logo-section">
              <img src="/soc2experts_logo.png" alt="SOC2 Experts" className="logo-image" />
            </div>
          </div>
        </header>
        <div className="content">
          <PasswordResetForm 
            token={resetToken} 
            onClose={() => {
              window.location.href = '/';
            }} 
          />
        </div>
      </div>
    );
  }

  // Handle payment result pages - moved after all hooks
  if (currentPath === '/payment-success') {
    return <PaymentSuccess onLogin={handleLogin} />;
  }
  if (currentPath === '/payment-cancelled') {
    return <PaymentCancelled />;
  }



  if (loading) return (
    <div className="app">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <div className="loading-text">{loadingMessage}</div>
        <div className="loading-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p style={{color: '#ffffff', fontWeight: '500', fontSize: '1rem'}}>
            Connecting to backend and fetching criteria data...
          </p>
          <p style={{fontSize: '0.9rem', color: '#e0e0e0', marginTop: '10px'}}>
            The server may need a moment to initialize. Please be patient...
          </p>
        </div>
        <div className="joke-display" style={{
          marginTop: '30px',
          padding: '20px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '10px',
          maxWidth: '500px',
          textAlign: 'center',
          fontSize: '0.95rem',
          lineHeight: '1.4',
          color: '#ffffff',
          minHeight: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {soc2Jokes[currentJoke]}
        </div>
      </div>
    </div>
  );

  if (error) return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <img src="/soc2experts_logo.png" alt="SOC2 Experts" className="logo-image" />
          </div>
        </div>
      </header>
      <div className="error-container">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Connection Issue</h2>
          <p className="error-message">{error}</p>
          <div className="error-actions">
            <button 
              className="retry-btn"
              onClick={() => {
                setError(null);
                setLoading(true);
                setLoadingMessage('Retrying connection...');
                fetchCriteria(0);
              }}
            >
              Try Again
            </button>
            <button 
              className="refresh-btn"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
          <div className="error-help">
            <p>If the problem persists:</p>
            <ul>
              <li>Check your internet connection</li>
              <li>Wait a moment and try again</li>
              <li>Contact support at info@soc2experts.com</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="logo-section">
            <img src="/soc2experts_logo.png" alt="SOC2 Experts" className="logo-image" />
          </div>
          <div className="user-info">
            {user ? (
              <>
                <span>Welcome, {user.first_name} {user.last_name}</span>
                <span className="company">({user.company_name})</span>
                <span className="role">[{user.role_name}]</span>
                <button onClick={handleLogout} className="logout-btn">Logout</button>
              </>
            ) : (
              <button onClick={() => setShowLogin(true)} className="login-btn">Login</button>
            )}
          </div>
        </div>
      </header>

      <div className="main-tabs">
        {(user ? mainTabsLoggedIn : mainTabsLoggedOut).map(tab => (
          <button
            key={tab.code}
            className={`main-tab ${activeMainTab === tab.code ? 'active' : ''}`}
            data-main-tab={tab.code}
            onClick={() => handleMainTabClick(tab.code)}
          >
            {tab.name}
          </button>
        ))}
      </div>

      

      {activeMainTab === 'soc2prep' && !user && (
        <div className="soc2prep-tabs">
          {prepServiceTabs.map(tab => (
            <button
              key={tab.code}
              className={`soc2prep-tab ${activePrepTab === tab.code ? 'active' : ''}`}
              data-prep-tab={tab.code}
              onClick={() => handlePrepTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'soc2prep' && user && (
        <div className="dashboard-tabs">
          {prepDashboardTabs.map(tab => (
            <button
              key={tab.code}
              className={`dashboard-tab ${activeDashboardTab === tab.code ? 'active' : ''}`}
              data-dashboard-tab={tab.code}
              onClick={() => handleDashboardTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'general' && user && (
        <div className="general-tabs">
          {generalTabs.map(tab => (
            <button
              key={tab.code}
              className={`general-tab ${activeGeneralTab === tab.code ? 'active' : ''}`}
              data-general-tab={tab.code}
              onClick={() => handleGeneralTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {(activeMainTab === 'security' || activeMainTab === 'availability' || activeMainTab === 'processing' || activeMainTab === 'confidentiality' || activeMainTab === 'privacy') && user && (
        <div className="trust-criteria-tabs">
          {trustCriteriaTabs.map(tab => (
            <button
              key={tab.code}
              className={`trust-criteria-tab ${activeTrustCriteriaTab === tab.code ? 'active' : ''}`}
              data-trust-criteria-tab={tab.code}
              onClick={() => handleTrustCriteriaTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'customer' && (
        <div className="customer-tabs">
          {customerTabs.map(tab => (
            <button
              key={tab.code}
              className={`customer-tab ${activeCustomerTab === tab.code ? 'active' : ''}`}
              data-customer-tab={tab.code}
              onClick={() => handleCustomerTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'about' && (
        <div className="about-tabs">
          {aboutTabs.map(tab => (
            <button
              key={tab.code}
              className={`about-tab ${activeAboutTab === tab.code ? 'active' : ''}`}
              data-about-tab={tab.code}
              onClick={() => handleAboutTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'faq' && (
        <div className="faq-tabs">
          {faqTabs.map(tab => (
            <button
              key={tab.code}
              className={`faq-tab ${activeFaqTab === tab.code ? 'active' : ''}`}
              data-faq-tab={tab.code}
              onClick={() => handleFaqTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      {activeMainTab === 'contact' && (
        <div className="contact-tabs">
          {contactTabs.map(tab => (
            <button
              key={tab.code}
              className={`contact-tab ${activeContactTab === tab.code ? 'active' : ''}`}
              data-contact-tab={tab.code}
              onClick={() => handleContactTabClick(tab.code)}
            >
              {tab.name}
            </button>
          ))}
        </div>
      )}

      <div className="content">
        <div className="tab-description">
          {activeMainTab === 'soc2prep' && !user ? (
            <>
              <h2>{getCurrentPrepTab()?.name}</h2>
              <p>{getCurrentPrepTab()?.description}</p>
            </>
          ) : activeMainTab === 'general' && user ? (
            <>
              <h2>{getCurrentGeneralTab()?.name}</h2>
              <p>{getCurrentGeneralTab()?.description}</p>
            </>
          ) : (activeMainTab === 'security' || activeMainTab === 'availability' || activeMainTab === 'processing' || activeMainTab === 'confidentiality' || activeMainTab === 'privacy') && user ? (
            <>
              <h2>{getCurrentMainTab()?.name} - {getCurrentTrustCriteriaTab()?.name}</h2>
              <p>{getCurrentTrustCriteriaTab()?.description}</p>
            </>
          ) : activeMainTab === 'customer' ? (
            <>
              <h2>{getCurrentCustomerTab()?.name}</h2>
              <p>{getCurrentCustomerTab()?.description}</p>
            </>
          ) : activeMainTab === 'about' ? (
            <>
              <h2>{getCurrentAboutTab()?.name}</h2>
              <p>{getCurrentAboutTab()?.description}</p>
            </>
          ) : activeMainTab === 'faq' ? (
            <>
              <h2>{getCurrentFaqTab()?.name}</h2>
              <p>{getCurrentFaqTab()?.description}</p>
            </>
          ) : activeMainTab === 'contact' ? (
            <>
              <h2>{getCurrentContactTab()?.name}</h2>
              <p>{getCurrentContactTab()?.description}</p>
            </>
          ) : (
            <>
              <h2>{getCurrentMainTab()?.name}</h2>
              <p>{getCurrentMainTab()?.description}</p>
            </>
          )}
        </div>

        {activeMainTab === 'general' && user && (
          <div className="general-content">
            <div className="content-section">
              {activeGeneralTab === 'dashboard' && (
                <>
                  <h3>SOC 2 Compliance Dashboard</h3>
                  <div className="dashboard-overview">
                    <div className="readiness-score">
                      <h4>Audit Readiness Score</h4>
                      <div className="score-display">
                        <span className="score">{dashboardStats.readinessScore}%</span>
                        <span className="score-label">
                          {dashboardStats.readinessScore >= 90 ? 'Audit Ready' :
                           dashboardStats.readinessScore >= 70 ? 'Nearly Ready' :
                           dashboardStats.readinessScore >= 50 ? 'In Progress' : 'Getting Started'}
                        </span>
                      </div>
                    </div>
                    <div className="dashboard-stats">
                      <div className="stat-card">
                        <h5>Policies Uploaded</h5>
                        <div className="stat-number">{dashboardStats.policies.completed}/{dashboardStats.policies.total}</div>
                      </div>
                      <div className="stat-card">
                        <h5>Questions Completed</h5>
                        <div className="stat-number">{dashboardStats.questions.completed}/{dashboardStats.questions.total}</div>
                      </div>
                      <div className="stat-card">
                        <h5>Evidence Items</h5>
                        <div className="stat-number">{dashboardStats.evidence.completed}/{dashboardStats.evidence.total}</div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeGeneralTab === 'business' && (
                <>
                  <h3>Business Information</h3>
                  <div className="business-info-form">
                    <div className="form-section">
                      <h4>Company Details</h4>
                      <div className="form-group">
                        <label>Company Name:</label>
                        <input type="text" defaultValue={user.company_name} disabled/>
                      </div>
                      <div className="form-group">
                        <label>Company Logo:</label>
                        <input type="file" accept="image/*" onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            handleBusinessInfoChange('logo_url', URL.createObjectURL(e.target.files[0]));
                          }
                        }}/>
                        <small>Upload your company logo (PNG, JPG, SVG)</small>
                      </div>
                      <div className="form-group">
                        <label>Head Office Address:</label>
                        <textarea placeholder="Enter your company's head office address..." rows="3" value={businessInfo.head_office_address} onChange={(e) => handleBusinessInfoChange('head_office_address', e.target.value)}></textarea>
                      </div>
                      <div className="form-group">
                        <label>Services/Products Description:</label>
                        <textarea placeholder="Describe your main services and products..." rows="4" value={businessInfo.services_description} onChange={(e) => handleBusinessInfoChange('services_description', e.target.value)}></textarea>
                      </div>
                      <div className="form-group">
                        <label>Industry:</label>
                        <select value={businessInfo.industry} onChange={(e) => handleBusinessInfoChange('industry', e.target.value)}>
                          <option value="">Select industry...</option>
                          <option value="technology">Technology</option>
                          <option value="healthcare">Healthcare</option>
                          <option value="financial">Financial Services</option>
                          <option value="retail">Retail</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <button className="save-btn" disabled={isSavingBusinessInfo} onClick={saveBusinessInfo}>
                        {isSavingBusinessInfo ? 'Saving...' : 'Save Changes'}
                      </button>
                      {businessInfoMessage && (
                        <div className={`save-message ${businessInfoMessage.includes('Error') ? 'error' : 'success'}`}>
                          {businessInfoMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeGeneralTab === 'team' && (
                <Suspense fallback={<div className="loading">Loading team management...</div>}>
                  <h3>Team Management</h3>
                  <TeamManagement 
                    user={user} 
                    onTeamChange={() => fetchAndCacheTeamMembers(true)}
                    cachedData={cachedData.teamMembers}
                  />
                </Suspense>
              )}

              {activeGeneralTab === 'policies' && (
                <Suspense fallback={<div className="loading">Loading policy management...</div>}>
                  <h3>Policy Management</h3>
                  <PolicyManagement 
                    user={user} 
                    onPolicyChange={() => {
                      fetchDashboardStats();
                      fetchAndCachePolicies(true);
                    }}
                    cachedData={cachedData.policies}
                  />
                </Suspense>
              )}

              {activeGeneralTab === 'billing' && (
                <>
                  <h3>Billing & Payment</h3>
                  <div className="billing-section">
                    <div className="current-plan">
                      <h4>Current Plan: Professional</h4>
                      <div className="plan-details">
                        <p><strong>$5,000</strong> per year</p>
                        <p>Up to 125 team members</p>
                        <p>3 expert consultations included</p>
                        <p>Next billing date: March 15, 2025</p>
                      </div>
                    </div>
                    <div className="payment-methods">
                      <h4>Payment Methods</h4>
                      <div className="payment-method">
                        <div className="card-info">
                          <strong>•••• •••• •••• 4242</strong>
                          <span>Expires 12/26</span>
                        </div>
                        <div className="card-actions">
                          <button className="edit-btn">Edit</button>
                          <button className="remove-btn">Remove</button>
                        </div>
                      </div>
                      <button className="add-payment-btn">Add Payment Method</button>
                    </div>
                    <div className="billing-history">
                      <h4>Billing History</h4>
                      <div className="invoice-list">
                        <div className="invoice-item">
                          <div className="invoice-info">
                            <strong>Invoice #INV-2025-001</strong>
                            <span>January 15, 2025</span>
                          </div>
                          <div className="invoice-amount">$5,000.00</div>
                          <div className="invoice-status paid">Paid</div>
                          <button className="download-btn">Download</button>
                        </div>
                        <div className="invoice-item">
                          <div className="invoice-info">
                            <strong>Invoice #INV-2024-012</strong>
                            <span>December 15, 2024</span>
                          </div>
                          <div className="invoice-amount">$5,000.00</div>
                          <div className="invoice-status paid">Paid</div>
                          <button className="download-btn">Download</button>
                        </div>
                      </div>
                    </div>
                    <div className="billing-actions">
                      <button className="cancel-subscription-btn">Cancel Subscription</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {(activeMainTab === 'security' || activeMainTab === 'availability' || activeMainTab === 'processing' || activeMainTab === 'confidentiality' || activeMainTab === 'privacy') && user && (
          <div className="trust-criteria-content">
            <div className="content-section">
              {activeTrustCriteriaTab === 'policies' && (
                <Suspense fallback={<div className="loading">Loading policy management...</div>}>
                  <h3>{getCurrentMainTab()?.name} - Policies</h3>
                  <PolicyManagement 
                    user={user} 
                    trustCriterion={activeMainTab} 
                    onPolicyChange={() => {
                      fetchDashboardStats();
                      fetchAndCachePolicies(true);
                    }}
                    cachedData={cachedData.policies}
                  />
                </Suspense>
              )}

              {activeTrustCriteriaTab === 'questions' && (
                <Suspense fallback={<div className="loading">Loading questions...</div>}>
                  <h3>{getCurrentMainTab()?.name} - Assessment Questions</h3>
                  <QuestionsManagement 
                    user={user} 
                    trustCriterion={activeMainTab} 
                    onQuestionChange={() => {
                      fetchDashboardStats();
                      fetchAndCacheQuestions(true);
                    }}
                    cachedData={cachedData.questions}
                  />
                </Suspense>
              )}

              {activeTrustCriteriaTab === 'evidence' && (
                <>
                  <div className="evidence-header">
                    <h3>{getCurrentMainTab()?.name} - Evidence Collection</h3>
                    <button onClick={initializeEvidence} className="init-evidence-btn">
                      Initialize Evidence Requirements
                    </button>
                  </div>

                  <div className="evidence-tabs">
                    <button 
                      className={`evidence-tab ${evidenceTab === 'all' ? 'active' : ''}`}
                      onClick={() => setEvidenceTab('all')}
                    >
                      All Evidence ({evidence.length})
                    </button>
                    <button 
                      className={`evidence-tab ${evidenceTab === 'missing' ? 'active' : ''}`}
                      onClick={() => setEvidenceTab('missing')}
                    >
                      Missing ({evidence.filter(e => e.status === 'missing').length})
                    </button>
                    <button 
                      className={`evidence-tab ${evidenceTab === 'collected' ? 'active' : ''}`}
                      onClick={() => setEvidenceTab('collected')}
                    >
                      Collected ({evidence.filter(e => e.status === 'collected').length})
                    </button>
                  </div>

                  <div className="evidence-progress">
                    <h4>Evidence Collection Progress: {evidence.filter(e => e.status === 'collected').length}/{evidence.length} Items</h4>
                    <div className="progress-bar">
                      <div className="progress-fill-static" style={{
                        width: evidence.length > 0 ? `${(evidence.filter(e => e.status === 'collected').length / evidence.length) * 100}%` : '0%',
                        height: '100%',
                        backgroundColor: '#667eea',
                        borderRadius: '2px',
                        transition: 'none'
                      }}></div>
                    </div>
                  </div>

                  <div className="evidence-section">
                    {(() => {
                      const trustCriterionMapping = {
                        'security': 'CC',
                        'availability': 'A',
                        'processing': 'PI',
                        'confidentiality': 'C',
                        'privacy': 'P'
                      };
                      
                      const filteredEvidence = evidence.filter(e => 
                        e.category_code === trustCriterionMapping[activeMainTab]
                      ).filter(e => {
                        if (evidenceTab === 'missing') return e.status === 'missing';
                        if (evidenceTab === 'collected') return e.status === 'collected';
                        return true;
                      });

                      const groupedEvidence = filteredEvidence.reduce((groups, item) => {
                        const key = `${item.category_name} - ${item.criteria_title}`;
                        if (!groups[key]) {
                          groups[key] = [];
                        }
                        groups[key].push(item);
                        return groups;
                      }, {});

                      return Object.entries(groupedEvidence).map(([groupName, items]) => (
                        <div key={groupName} className="evidence-category">
                          <h5>{groupName}</h5>
                          <div className="evidence-items">
                            {items.map((evidenceItem, index) => (
                              <div key={index} className={`evidence-item ${evidenceItem.status || 'missing'}`}>
                                <div className="evidence-info">
                                  <strong>{evidenceItem.evidence_description}</strong>
                                  <p>
                                    {evidenceItem.point_of_focus_title} ({evidenceItem.point_of_focus_code})
                                  </p>
                                  {evidenceItem.original_filename && (
                                    <p className="evidence-file">
                                      📄 {evidenceItem.original_filename}
                                      {evidenceItem.collected_by_name && (
                                        <span> - Collected by {evidenceItem.collected_by_name}</span>
                                      )}
                                    </p>
                                  )}
                                  {evidenceItem.notes && (
                                    <p className="evidence-notes">Notes: {evidenceItem.notes}</p>
                                  )}
                                </div>

                                <div className="evidence-actions">
                                  <span className={`status-badge ${evidenceItem.status || 'missing'}`}>
                                    {evidenceItem.status === 'collected' ? '✓ Collected' :
                                     evidenceItem.status === 'not_applicable' ? 'N/A' : 'Missing'}
                                  </span>

                                  {evidenceItem.status !== 'collected' && (
                                    <div className="evidence-upload">
                                      <input
                                        type="file"
                                        id={`evidence-${evidenceItem.evidence_id || index}`}
                                        style={{ display: 'none' }}
                                        onChange={(e) => {
                                          if (e.target.files[0] && evidenceItem.evidence_id) {
                                            uploadEvidenceFile(evidenceItem.evidence_id, e.target.files[0]);
                                          }
                                        }}
                                      />
                                      <button
                                        onClick={() => document.getElementById(`evidence-${evidenceItem.evidence_id || index}`).click()}
                                        className="upload-evidence-btn"
                                        disabled={uploadingEvidence[evidenceItem.evidence_id] || !evidenceItem.evidence_id}
                                      >
                                        {uploadingEvidence[evidenceItem.evidence_id] ? 'Uploading...' : 'Upload File'}
                                      </button>

                                      {evidenceItem.evidence_id && (
                                        <button
                                          onClick={() => updateEvidenceStatus(evidenceItem.evidence_id, 'not_applicable')}
                                          className="na-btn"
                                        >
                                          Mark N/A
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeMainTab === 'soc2prep' && (
          <div className="soc2prep-content">
            {user ? (
              <div className="content-section">
                {activeDashboardTab === 'dashboard' && (
                  <>
                    <h3>SOC 2 Prep Service Dashboard</h3>
                    <div className="dashboard-overview">
                      <div className="readiness-score">
                        <h4>Audit Readiness Score</h4>
                        <div className="score-display">
                          <span className="score">{dashboardStats.readinessScore}%</span>
                          <span className="score-label">
                            {dashboardStats.readinessScore >= 90 ? 'Audit Ready' :
                             dashboardStats.readinessScore >= 70 ? 'Nearly Ready' :
                             dashboardStats.readinessScore >= 50 ? 'In Progress' : 'Getting Started'}
                          </span>
                        </div>
                      </div>
                      <div className="dashboard-stats">
                        <div className="stat-card">
                          <h5>Policies Uploaded</h5>
                          <div className="stat-number">{dashboardStats.policies.completed}/{dashboardStats.policies.total}</div>
                        </div>
                        <div className="stat-card">
                          <h5>Questions Completed</h5>
                          <div className="stat-number">{dashboardStats.questions.completed}/{dashboardStats.questions.total}</div>
                        </div>
                        <div className="stat-card">
                          <h5>Evidence Items</h5>
                          <div className="stat-number">{dashboardStats.evidence.completed}/{dashboardStats.evidence.total}</div>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {activeDashboardTab === 'business' && (
                  <>
                    <h3>Business Information</h3>
                    <div className="business-info-form">
                      <div className="form-section">
                        <h4>Company Details</h4>
                        <div className="form-group">
                          <label>Company Name:</label>
                          <input type="text" defaultValue={user.company_name}  disabled/>
                        </div>
                        <div className="form-group">
                          <label>Company Logo:</label>
                          <input type="file" accept="image/*" onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleBusinessInfoChange('logo_url', URL.createObjectURL(e.target.files[0]));
                            }
                          }}/>
                          <small>Upload your company logo (PNG, JPG, SVG)</small>
                        </div>
                        <div className="form-group">
                          <label>Head Office Address:</label>
                          <textarea placeholder="Enter your company's head office address..." rows="3" value={businessInfo.head_office_address} onChange={(e) => handleBusinessInfoChange('head_office_address', e.target.value)}></textarea>
                        </div>
                        <div className="form-group">
                          <label>Services/Products Description:</label>
                          <textarea placeholder="Describe your main services and products..." rows="4"  value={businessInfo.services_description} onChange={(e) => handleBusinessInfoChange('services_description', e.target.value)}></textarea>
                        </div>
                        <div className="form-group">
                          <label>Industry:</label>
                          <select value={businessInfo.industry} onChange={(e) => handleBusinessInfoChange('industry', e.target.value)}>
                            <option value="">Select industry...</option>
                            <option value="technology">Technology</option>
                            <option value="healthcare">Healthcare</option>
                            <option value="financial">Financial Services</option>
                            <option value="retail">Retail</option>
                            <option value="manufacturing">Manufacturing</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <button className="save-btn" disabled={isSavingBusinessInfo} onClick={() => {
                          saveBusinessInfo();
                          // Refresh cache after save
                          setTimeout(() => fetchAndCacheBusinessInfo(true), 1000);
                        }}>
                          {isSavingBusinessInfo ? 'Saving...' : 'Save Changes'}
                        </button>
                        {businessInfoMessage && (
                          <div className={`save-message ${businessInfoMessage.includes('Error') ? 'error' : 'success'}`}>
                            {businessInfoMessage}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {activeDashboardTab === 'team' && (
                  <Suspense fallback={<div className="loading">Loading team management...</div>}>
                    <h3>Team Management</h3>
                    <TeamManagement 
                      user={user} 
                      onTeamChange={() => fetchAndCacheTeamMembers(true)}
                      cachedData={cachedData.teamMembers}
                    />
                  </Suspense>
                )}

                {activeDashboardTab === 'policies' && (
                  <Suspense fallback={<div className="loading">Loading policy management...</div>}>
                    <h3>Policy Management</h3>
                    <PolicyManagement 
                      user={user} 
                      onPolicyChange={() => {
                        fetchDashboardStats();
                        fetchAndCachePolicies(true);
                      }}
                      cachedData={cachedData.policies}
                    />
                  </Suspense>
                )}

                {activeDashboardTab === 'questions' && (
                  <Suspense fallback={<div className="loading">Loading questions...</div>}>
                    <h3>SOC 2 Assessment Questions</h3>
                    <QuestionsManagement 
                      user={user} 
                      onQuestionChange={() => {
                        fetchDashboardStats();
                        fetchAndCacheQuestions(true);
                      }}
                      cachedData={cachedData.questions}
                    />
                  </Suspense>
                )}

                {activeDashboardTab === 'evidence' && (
                  <>
                    <div className="evidence-header">
                      <h3>Evidence Collection</h3>
                      <button onClick={initializeEvidence} className="init-evidence-btn">
                        Initialize Evidence Requirements
                      </button>
                    </div>

                    <div className="evidence-tabs">
                      <button 
                        className={`evidence-tab ${evidenceTab === 'all' ? 'active' : ''}`}
                        onClick={() => setEvidenceTab('all')}
                      >
                        All Evidence ({evidence.length})
                      </button>
                      <button 
                        className={`evidence-tab ${evidenceTab === 'missing' ? 'active' : ''}`}
                        onClick={() => setEvidenceTab('missing')}
                      >
                        Missing ({evidence.filter(e => e.status === 'missing').length})
                      </button>
                      <button 
                        className={`evidence-tab ${evidenceTab === 'collected' ? 'active' : ''}`}
                        onClick={() => setEvidenceTab('collected')}
                      >
                        Collected ({evidence.filter(e => e.status === 'collected').length})
                      </button>
                    </div>

                    <div className="evidence-progress">
                      <h4>Evidence Collection Progress: {evidence.filter(e => e.status === 'collected').length}/{evidence.length} Items</h4>
                      <div className="progress-bar">
                        <div className="progress-fill-static" style={{
                          width: evidence.length > 0 ? `${(evidence.filter(e => e.status === 'collected').length / evidence.length) * 100}%` : '0%',
                          height: '100%',
                          backgroundColor: '#667eea',
                          borderRadius: '2px',
                          transition: 'none'
                        }}></div>
                      </div>
                    </div>

                    <div className="evidence-section">
                      {(() => {
                        const filteredEvidence = evidenceTab === 'all' ? evidence :
                          evidenceTab === 'missing' ? evidence.filter(e => e.status === 'missing') :
                          evidence.filter(e => e.status === 'collected');

                        const groupedEvidence = filteredEvidence.reduce((groups, item) => {
                          const key = `${item.category_name} - ${item.criteria_title}`;
                          if (!groups[key]) {
                            groups[key] = [];
                          }
                          groups[key].push(item);
                          return groups;
                        }, {});

                        return Object.entries(groupedEvidence).map(([groupName, items]) => (
                          <div key={groupName} className="evidence-category">
                            <h5>{groupName}</h5>
                            <div className="evidence-items">
                              {items.map((evidenceItem, index) => (
                                <div key={index} className={`evidence-item ${evidenceItem.status || 'missing'}`}>
                                  <div className="evidence-info">
                                    <strong>{evidenceItem.evidence_description}</strong>
                                    <p>
                                      {evidenceItem.point_of_focus_title} ({evidenceItem.point_of_focus_code})
                                    </p>
                                    {evidenceItem.original_filename && (
                                      <p className="evidence-file">
                                        📄 {evidenceItem.original_filename}
                                        {evidenceItem.collected_by_name && (
                                          <span> - Collected by {evidenceItem.collected_by_name}</span>
                                        )}
                                      </p>
                                    )}
                                    {evidenceItem.notes && (
                                      <p className="evidence-notes">Notes: {evidenceItem.notes}</p>
                                    )}
                                  </div>

                                  <div className="evidence-actions">
                                    <span className={`status-badge ${evidenceItem.status || 'missing'}`}>
                                      {evidenceItem.status === 'collected' ? '✓ Collected' :
                                       evidenceItem.status === 'not_applicable' ? 'N/A' : 'Missing'}
                                    </span>

                                    {evidenceItem.status !== 'collected' && (
                                      <div className="evidence-upload">
                                        <input
                                          type="file"
                                          id={`evidence-${evidenceItem.evidence_id || index}`}
                                          style={{ display: 'none' }}
                                          onChange={(e) => {
                                            if (e.target.files[0] && evidenceItem.evidence_id) {
                                              uploadEvidenceFile(evidenceItem.evidence_id, e.target.files[0]);
                                            }
                                          }}
                                        />
                                        <button
                                          onClick={() => document.getElementById(`evidence-${evidenceItem.evidence_id || index}`).click()}
                                          className="upload-evidence-btn"
                                          disabled={uploadingEvidence[evidenceItem.evidence_id] || !evidenceItem.evidence_id}
                                        >
                                          {uploadingEvidence[evidenceItem.evidence_id] ? 'Uploading...' : 'Upload File'}
                                        </button>

                                        {evidenceItem.evidence_id && (
                                          <button
                                            onClick={() => updateEvidenceStatus(evidenceItem.evidence_id, 'not_applicable')}
                                            className="na-btn"
                                          >
                                            Mark N/A
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </>
                )}

                {activeDashboardTab === 'billing' && (
                  <>
                    <h3>Billing & Payment</h3>
                    <div className="billing-section">
                      <div className="current-plan">
                        <h4>Current Plan: Professional</h4>
                        <div className="plan-details">
                          <p><strong>$5,000</strong> per year</p>
                          <p>Up to 125 team members</p>
                          <p>3 expert consultations included</p>
                          <p>Next billing date: March 15, 2025</p>
                        </div>
                      </div>
                      <div className="payment-methods">
                        <h4>Payment Methods</h4>
                        <div className="payment-method">
                          <div className="card-info">
                            <strong>•••• •••• •••• 4242</strong>
                            <span>Expires 12/26</span>
                          </div>
                          <div className="card-actions">
                            <button className="edit-btn">Edit</button>
                            <button className="remove-btn">Remove</button>
                          </div>
                        </div>
                        <button className="add-payment-btn">Add Payment Method</button>
                      </div>
                      <div className="billing-history">
                        <h4>Billing History</h4>
                        <div className="invoice-list">
                          <div className="invoice-item">
                            <div className="invoice-info">
                              <strong>Invoice #INV-2025-001</strong>
                              <span>January 15, 2025</span>
                            </div>
                            <div className="invoice-amount">$5,000.00</div>
                            <div className="invoice-status paid">Paid</div>
                            <button className="download-btn">Download</button>
                          </div>
                          <div className="invoice-item">
                            <div className="invoice-info">
                              <strong>Invoice #INV-2024-012</strong>
                              <span>December 15, 2024</span>
                            </div>
                            <div className="invoice-amount">$5,000.00</div>
                            <div className="invoice-status paid">Paid</div>
                            <button className="download-btn">Download</button>
                          </div>
                        </div>
                      </div>
                      <div className="billing-actions">
                        <button className="cancel-subscription-btn">Cancel Subscription</button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="content-section">
                {activePrepTab === 'description' && (
                  <>
                    <h3>Fractional Executive Compliance Services</h3>
                    <p>Get the expertise of a Chief Information Security Officer (CISO), Chief Information Officer (CIO), or Privacy Officer without the full-time cost. Our comprehensive SOC 2 preparation service combines powerful tools with expert human guidance.</p>

                    <div className="service-features">
                      <div className="feature-card">
                        <h4>Fractional CISO Guidance</h4>
                        <p>Access experienced security leadership to guide your compliance strategy, risk assessments, and audit preparation - like having a part-time CISO on your team.</p>
                      </div>
                      <div className="feature-card">
                        <h4>Tools + Human Approach</h4>
                        <p>Powerful automation for evidence collection and tracking, combined with expert human oversight and strategic guidance.</p>
                      </div>
                      <div className="feature-card">
                        <h4>Executive-Level Readiness</h4>
                        <p>Receive board-ready compliance reports and executive briefings that demonstrate your audit readiness to stakeholders and investors.</p>
                      </div>
                      <div className="feature-card">
                        <h4>Strategic Gap Analysis</h4>
                        <p>Get C-level perspective on your compliance gaps with prioritized remediation plans that align with your business objectives.</p>
                      </div>
                      <div className="feature-card">
                        <h4>Fractional Privacy Officer</h4>
                        <p>Access privacy expertise for GDPR, CCPA, and other privacy frameworks - like having a dedicated Privacy Officer guiding your data protection strategy.</p>
                      </div>
                      <div className="feature-card">
                        <h4>Scalable Leadership</h4>
                        <p>From DIY tools to dedicated fractional executive services, get the right level of compliance leadership for your company's stage and budget.</p>
                      </div>
                    </div>

                    <div className="section-nav-container">
                      <button 
                        className="section-nav-btn"
                        onClick={() => handlePrepTabClick('signup')}
                      >
                        Next: Sign Up & Pricing →
                      </button>
                    </div>
                  </>
                )}

                

                {activePrepTab === 'privacy' && (
                  <>
                    <h3>Privacy Policy</h3>
                    <div className="policy-content">
                      <p><strong>LocalPro, Inc.</strong> operates the soc2experts.com website and provides SOC 2 preparation services. This privacy policy describes how we collect, use, and protect your information.</p>

                      <h4>Information We Collect</h4>
                      <p>We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.</p>

                      <h5>Personal Information:</h5>
                      <ul>
                        <li>Name, email address, and contact information</li>
                        <li>Company information and business details</li>
                        <li>Payment and billing information</li>
                        <li>Communications with our support team</li>
                      </ul>

                      <h5>Usage Information:</h5>
                      <ul>
                        <li>Information about how you use our services</li>
                        <li>Log data and technical information</li>
                        <li>Device and browser information</li>
                      </ul>

                      <h4>How We Use Your Information</h4>
                      <ul>
                        <li>Provide and improve our SOC 2 preparation services</li>
                        <li>Process payments and manage your account</li>
                        <li>Communicate with you about our services</li>
                        <li>Comply with legal obligations</li>
                        <li>Protect against fraud and security threats</li>
                      </ul>

                      <h4>Information Sharing</h4>
                      <p>LocalPro, Inc. does not sell, trade, or rent your personal information to third parties. We may share information in the following circumstances:</p>
                      <ul>
                        <li>With your consent</li>
                        <li>To comply with legal requirements</li>
                        <li>To protect our rights and safety</li>
                        <li>With service providers who assist us in operations</li>
                      </ul>

                      <h4>Data Security</h4>
                      <p>LocalPro, Inc. implements appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction.</p>

                      <h4>Your Rights</h4>
                      <p>You have the right to access, update, or delete your personal information. Contact us at privacy@soc2experts.com for assistance.</p>

                      <h4>Contact Information</h4>
                      <p>For privacy-related questions or concerns, contact LocalPro, Inc. at:</p>
                      <ul>
                        <li>Email: privacy@soc2experts.com</li>
                        <li>Phone: (415) 906-6005</li>
                      </ul>

                      <p><em>Last updated: January 2025</em></p>
                    </div>

                    <div className="section-nav-container">
                      <button 
                        className="section-nav-btn"
                        onClick={() => handlePrepTabClick('terms')}
                      >
                        Next: Terms of Service →
                      </button>
                    </div>
                  </>
                )}

                {activePrepTab === 'terms' && (
                  <>
                    <h3>Terms of Service</h3>
                    <div className="policy-content">
                      <h4>Service Provider</h4>
                      <p>These terms govern your use of the SOC 2 preparation services provided by <strong>LocalPro, Inc.</strong> through the soc2experts.com website. By accessing and using our services, you enter into a binding agreement with LocalPro, Inc.</p>

                      <h4>Acceptance of Terms</h4>
                      <p>By accessing and using our SOC 2 preparation service, you accept and agree to be bound by the terms and provision of this agreement with LocalPro, Inc.</p>

                      <h4>Service Description</h4>
                      <p>LocalPro, Inc. provides SOC 2 audit preparation tools, guidance, and consultation to help organizations prepare for SOC 2 compliance audits.</p>

                      <h4>Service Guarantees</h4>
                      <p>Subject to the client requirements and limitations outlined below, LocalPro, Inc. provides the following guarantees:</p>
                      <ul>
                        <li><strong>SOC 2 Readiness Preparation:</strong> We guarantee that organizations following our complete program will be audit-ready with all required policies, procedures, and evidence documentation</li>
                        <li><strong>Expert Guidance:</strong> We guarantee access to qualified SOC 2 experts with relevant certifications and audit experience</li>
                        <li><strong>Comprehensive Coverage:</strong> We guarantee our tools and guidance cover all applicable Trust Services Criteria (Security, Availability, Processing Integrity, Confidentiality, Privacy)</li>
                        <li><strong>Audit Support:</strong> We guarantee assistance during the audit process including auditor communication and evidence review</li>
                      </ul>

                      <h4>Limitations of Guarantees</h4>
                      <p>Our guarantees are subject to the following limitations:</p>
                      <ul>
                        <li><strong>Audit Outcomes:</strong> We cannot guarantee specific audit results, pass/fail outcomes, or auditor decisions</li>
                        <li><strong>Timeline Constraints:</strong> Guarantees apply only when sufficient time is allowed for proper implementation (minimum 90 days)</li>
                        <li><strong>Third-Party Dependencies:</strong> We cannot guarantee outcomes affected by third-party vendors, auditors, or external systems beyond our control</li>
                        <li><strong>Ongoing Compliance:</strong> Guarantees apply to readiness preparation only, not ongoing compliance maintenance</li>
                        <li><strong>Regulatory Changes:</strong> Guarantees do not cover compliance with regulatory changes that occur after service completion</li>
                        <li><strong>Client-Specific Circumstances:</strong> Guarantees may not apply to unique business models or technical environments requiring custom solutions</li>
                      </ul>

                      <h4>Client Requirements for Guarantee Coverage</h4>
                      <p>To maintain guarantee coverage, clients must:</p>
                      <ul>
                        <li><strong>Complete Engagement:</strong> Fully participate in all recommended activities, assessments, and implementation steps</li>
                        <li><strong>Accurate Information:</strong> Provide complete, accurate, and timely information about business processes, systems, and controls</li>
                        <li><strong>Timely Implementation:</strong> Implement recommended policies, procedures, and controls within agreed timeframes</li>
                        <li><strong>Management Commitment:</strong> Demonstrate executive leadership support and resource allocation for SOC 2 preparation</li>
                        <li><strong>Documentation Completion:</strong> Complete all required documentation, evidence collection, and policy acknowledgments</li>
                        <li><strong>Communication:</strong> Maintain regular communication and respond to requests for information within 5 business days</li>
                        <li><strong>Resource Allocation:</strong> Assign qualified personnel and adequate time for implementation activities</li>
                      </ul>

                      <h4>Events That Void Guarantees</h4>
                      <p>Guarantees become void if any of the following occur:</p>
                      <ul>
                        <li><strong>Incomplete Implementation:</strong> Failure to implement 80% or more of recommended controls and procedures</li>
                        <li><strong>Misrepresentation:</strong> Providing false or materially incomplete information about business operations or existing controls</li>
                        <li><strong>Scope Changes:</strong> Significant changes to business processes, systems, or scope after service commencement without proper assessment</li>
                        <li><strong>Non-Payment:</strong> Failure to maintain current payment status or breach of payment terms</li>
                        <li><strong>Auditor Issues:</strong> Selection of unqualified auditors or auditors with conflicts of interest despite our recommendations</li>
                        <li><strong>Force Majeure:</strong> Events beyond reasonable control including natural disasters, cyberattacks, or major regulatory changes</li>
                        <li><strong>Resource Constraints:</strong> Failure to allocate sufficient internal resources or personnel for implementation</li>
                        <li><strong>Timeline Violations:</strong> Proceeding with audit before completing recommended preparation period</li>
                        <li><strong>Third-Party Dependencies:</strong> Failure to address critical dependencies on third-party systems or vendors as recommended</li>
                      </ul>

                      <h4>User Responsibilities</h4>
                      <ul>
                        <li>Provide accurate and complete information about business operations and existing controls</li>
                        <li>Maintain the confidentiality of your account credentials</li>
                        <li>Use the service in compliance with applicable laws</li>
                        <li>Not share access credentials with unauthorized users</li>
                      </ul>

                      <h4>Payment and Billing</h4>
                      <ul>
                        <li>All contracts and billing are handled by LocalPro, Inc.</li>
                        <li>Annual subscription fees as selected during signup</li>
                        <li>Additional users charged at $120 per user per year</li>
                        <li>Payments are non-refundable after 30 days</li>
                        <li>Automatic renewal unless cancelled</li>
                      </ul>

                      <h4>Intellectual Property</h4>
                      <p>All content, tools, and materials provided through our service remain the intellectual property of LocalPro, Inc. You may use them solely for your SOC 2 preparation purposes.</p>

                      <h4>Limitation of Liability</h4>
                      <p>Our service is provided "as is" without warranties. LocalPro, Inc. is not liable for any damages arising from the use of our service or failure to achieve SOC 2 certification.</p>

                      <h4>Termination</h4>
                      <p>Either party may terminate this agreement with 30 days written notice to LocalPro, Inc. Upon termination, your access to the service will cease.</p>

                      <h4>Governing Law</h4>
                      <p>These terms are governed by the laws of the State of California, where LocalPro, Inc. is incorporated.</p>

                      <h4>Contact Information</h4>
                      <p>For questions about these terms, contact LocalPro, Inc. at:</p>
                      <ul>
                        <li>Email: legal@soc2experts.com</li>
                        <li>Phone: (415) 906-6005</li>
                      </ul>

                      <p><em>Last updated: January 2025</em></p>
                    </div>

                    <div className="section-nav-container">
                      <button 
                        className="section-nav-btn"
                        onClick={() => handlePrepTabClick('signup')}
                      >
                        Next: Sign Up →
                      </button>
                    </div>
                  </>
                )}

                {activePrepTab === 'signup' && (
                  <Suspense fallback={
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Loading sign-up options...</div>
                    </div>
                  }>
                    <SignupPage 
                      billingPeriod={activePricingBilling}
                      setBillingPeriod={setActivePricingBilling}
                      showFeatureComparison={showFeatureComparison}
                      setShowFeatureComparison={setShowFeatureComparison}
                    />
                  </Suspense>
                )}
              </div>
            )}
          </div>
        )}

        

        {activeMainTab === 'customer' && (
          <div className="customer-content">
            <div className="content-section">
              {activeCustomerTab === 'values' && (
                <>
                  <h3>Values of Our Ideal Customer</h3>
                  <p>We work best with organizations that share fundamental values about business excellence and integrity.</p>

                  <div className="values-grid">
                    <div className="value-item">
                      <h5>Quality Over Quick Fixes</h5>
                      <p>Companies that prioritize building robust, sustainable compliance programs rather than seeking the cheapest or fastest shortcuts. They understand that proper compliance is an investment in business credibility and long-term success.</p>
                    </div>
                    <div className="value-item">
                      <h5>Transparency and Honesty</h5>
                      <p>Organizations that value open communication and honest assessment of their current state. They're willing to acknowledge gaps and work collaboratively to address them systematically.</p>
                    </div>
                    <div className="value-item">
                      <h5>Strategic Investment Mindset</h5>
                      <p>Leadership that views compliance as a business enabler, not just a cost center. They understand that strong security and privacy practices are competitive advantages and customer trust builders.</p>
                    </div>
                    <div className="value-item">
                      <h5>Continuous Improvement</h5>
                      <p>Companies committed to ongoing refinement of their security posture and compliance programs. They see compliance as an evolving discipline, not a one-time checkbox exercise.</p>
                    </div>
                    <div className="value-item">
                      <h5>Employee-Centric Approach</h5>
                      <p>Organizations that prioritize their team's professional development and recognize that compliance success requires engaged, well-trained employees at all levels.</p>
                    </div>
                    <div className="value-item">
                      <h5>Customer Trust Priority</h5>
                      <p>Businesses that understand compliance certifications are ultimately about building and maintaining customer trust, and they're willing to invest appropriately in that relationship.</p>
                    </div>
                  </div>
                </>
              )}

              {activeCustomerTab === 'characteristics' && (
                <>
                  <h3>Characteristics of Companies We Best Serve</h3>
                  <p>Our services are most effective for organizations with specific characteristics and business situations.</p>

                  <div className="approach-grid">
                    <div className="approach-item">
                      <h5>Growing SaaS Companies</h5>
                      <p>Seed to Series B software companies (typically 10-200 employees) that are scaling rapidly and need compliance to unlock enterprise customers or meet investor requirements.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Resource-Conscious Teams</h5>
                      <p>Organizations that need executive-level compliance expertise but don't have the budget or need for a full-time CISO, CIO, or Privacy Officer. They value fractional leadership models.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Technology-Forward Companies</h5>
                      <p>Businesses that leverage cloud infrastructure (AWS, Google Cloud, Azure) and modern development practices. They're comfortable with API integrations and automated evidence collection.</p>
                    </div>
                    <div className="approach-item">
                      <h5>First-Time Audit Candidates</h5>
                      <p>Companies pursuing their first SOC 2 audit who need comprehensive preparation and guidance. They benefit most from our structured, educational approach to compliance.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Distributed or Remote Teams</h5>
                      <p>Organizations with remote or hybrid work arrangements that need cloud-based compliance management and virtual consultation services. Geographic flexibility is important to them.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Industry Agnostic</h5>
                      <p>While we work across industries, we particularly excel with fintech, healthtech, e-commerce, productivity software, and B2B platforms that handle sensitive customer data.</p>
                    </div>
                  </div>
                </>
              )}

              {activeCustomerTab === 'commitment' && (
                <>
                  <h3>What Commitment Means to Our Customers</h3>
                  <p>Successful compliance partnerships require genuine commitment from both sides. Here's what we expect from our ideal customers.</p>

                  <div className="approach-grid">
                    <div className="approach-item">
                      <h5>Leadership Engagement</h5>
                      <p>C-level executives and founders who personally engage with the compliance process. They attend key meetings, ask informed questions, and champion compliance initiatives internally.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Resource Allocation</h5>
                      <p>Willingness to dedicate appropriate internal resources - typically 10-20% of key team members' time during active preparation phases. They understand compliance isn't something that happens "to" them but "with" them.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Timeline Respect</h5>
                      <p>Understanding that quality compliance takes time - typically 3-6 months for proper preparation. They resist the urge to rush the process and appreciate methodical, thorough preparation.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Process Documentation</h5>
                      <p>Commitment to documenting and following established processes consistently. They understand that effective compliance requires discipline and systematic approaches, not ad-hoc solutions.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Investment in Tools</h5>
                      <p>Willingness to invest in proper security and compliance tools when recommended. They view technology spending as enabling business objectives, not unnecessary overhead.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Long-term Perspective</h5>
                      <p>Recognition that compliance is an ongoing journey, not a destination. They're interested in building sustainable programs that will serve them through multiple audit cycles and business growth phases.</p>
                    </div>
                  </div>

                  <div className="commitment-callout">
                    <h4>Our Commitment in Return</h4>
                    <p>When you demonstrate these commitments, we provide:</p>
                    <ul>
                      <li>Executive-level strategic guidance tailored to your business stage</li>
                      <li>Transparent communication about progress, challenges, and realistic timelines</li>
                      <li>Practical, implementable recommendations that fit your technical environment</li>
                      <li>Education and knowledge transfer so your team grows more capable over time</li>
                      <li>Ongoing support that scales with your business needs</li>
                      <li>Success guarantees that align our interests with your audit outcomes</li>
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeMainTab === 'about' && (
          <div className="about-content">
            <div className="content-section">
              {activeAboutTab === 'founder' && (
                <>
                  <h3>Who We Are - Founder</h3>
                  <div className="founders-section">
                    <div className="founder-card">
                      <div className="founder-photo">
                        <img src="/1556950724394.jpeg" alt="Case Jones - Founder" className="founder-image" />
                      </div>
                      <div className="founder-bio">
                        <h4>Case Jones - Founder</h4>
                        <p>Case brings over 25 years of technical leadership and cybersecurity expertise. As former VP of Information Security at Doxy.me, he led the organization through multiple successful SOC 2 Type II audits and HIPAA assessments. His extensive background includes full-stack development, system administration, enterprise security architecture, and compliance frameworks across healthcare, financial services, and technology companies.</p>
                        
                        <p>His financial services compliance experience includes direct exposure to bank-level compliance frameworks through enterprise DNS administration contracts at RBC (Royal Bank of Canada) and CIBC (Canadian Imperial Bank of Commerce), where he participated in ITIL processes for incident and change management. He also provided system administration for TD Bank's UGO digital wallet platform, gaining insight into banking compliance frameworks and regulatory requirements.</p>
                        
                        <p>With 5 years of experience running 24/7 call centers supporting information technology companies in Canada, Case has deep expertise in supporting SOC 2 compliance operations at scale. His international perspective comes from having lived in Ukraine and the United Arab Emirates, and he is currently based in Surrey, British Columbia.</p>
                        
                        <p>Case has managed large-scale infrastructures, led security teams, and implemented comprehensive compliance programs from the ground up. His practical approach to compliance stems from real-world experience helping companies navigate complex regulatory requirements while maintaining operational efficiency.</p>
                        
                        <p><a href="https://www.linkedin.com/in/casejones/" target="_blank" rel="noopener noreferrer">LinkedIn Profile</a></p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeAboutTab === 'mission' && (
                <>
                  <h3>Our Mission</h3>
                  <p>We're on a mission to deliver affordable high-quality compliance for all SaaS businesses.</p>

                  <h4>Our Approach: Tools + Human Involvement</h4>
                  <div className="approach-grid">
                    <div className="approach-item">
                      <h5>Fractional Executive Expertise</h5>
                      <p>Get the strategic guidance of a CISO, CIO, or Privacy Officer without the full-time cost - our experts act as your fractional compliance leadership.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Tools + Human Touch</h5>
                      <p>Powerful automation and templates combined with expert human guidance - the best of both worlds for efficient, effective compliance.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Business-Focused</h5>
                      <p>We understand that compliance is a business enabler, not just a checkbox exercise - like having a fractional executive who understands your goals.</p>
                    </div>
                    <div className="approach-item">
                      <h5>Scalable Leadership</h5>
                      <p>From DIY tools to dedicated fractional CISO services, we provide the right level of executive guidance for your stage and budget.</p>
                    </div>
                  </div>

                  <h4>Our Core Values</h4>
                  <div className="values-grid">
                    <div className="value-item">
                      <h5>Independent Governance</h5>
                      <p>The information security function must retain independence from other business operations to avoid conflicts of interest. Our services report directly to the board of directors or company owners, ensuring objective oversight and unbiased security assessments.</p>
                    </div>
                    <div className="value-item">
                      <h5>Absolute Discretion</h5>
                      <p>We maintain the highest standards of confidentiality due to the sensitive nature of security and compliance work. Your business information, vulnerabilities, and compliance status are protected with complete discretion.</p>
                    </div>
                    <div className="value-item">
                      <h5>Fractional Independence</h5>
                      <p>Our fractional service model provides the ideal structure for maintaining information security independence - you get dedicated expertise without the conflicts that can arise from internal reporting structures.</p>
                    </div>
                    <div className="value-item">
                      <h5>Board-Level Accountability</h5>
                      <p>All our services are designed to provide board-ready reporting and maintain the direct accountability that company leadership requires for effective security governance.</p>
                    </div>
                  </div>
                </>
              )}

              {activeAboutTab === 'whychoose' && (
                <>
                  <h3>Why Choose Us - Competitive Analysis</h3>
                  <p>Understanding your SOC 2 preparation options helps you make the right choice for your organization's needs and budget.</p>

                  <div className="competitive-analysis">
                    <div className="competitor-comparison">
                      <h4>SOC 2 Preparation Options Comparison</h4>
                      
                      <div className="option-comparison-grid">
                        <div className="option-card diy-approach">
                          <h5>❌ Full DIY Approach</h5>
                          <div className="option-details">
                            <div className="pros-cons">
                              <div className="pros">
                                <h6>Pros:</h6>
                                <ul>
                                  <li>Lower upfront cost perception</li>
                                  <li>Complete internal control</li>
                                  <li>Team learns compliance deeply</li>
                                </ul>
                              </div>
                              <div className="cons">
                                <h6>Cons:</h6>
                                <ul>
                                  <li>High risk of audit failure (60-70%)</li>
                                  <li>Massive time investment from executives</li>
                                  <li>Steep learning curve and mistakes</li>
                                  <li>No strategic guidance or mentorship</li>
                                  <li>Hidden costs often exceed $200,000</li>
                                </ul>
                              </div>
                            </div>
                            <div className="cost-reality">
                              <strong>Reality:</strong> $150,000-$300,000 true cost when factoring in executive time, mistakes, and re-audits
                            </div>
                          </div>
                        </div>

                        <div className="option-card big-consulting">
                          <h5>🏢 Big 4 / Traditional Consulting</h5>
                          <div className="option-details">
                            <div className="pros-cons">
                              <div className="pros">
                                <h6>Pros:</h6>
                                <ul>
                                  <li>Brand recognition and credibility</li>
                                  <li>Deep regulatory expertise</li>
                                  <li>Comprehensive audit services</li>
                                  <li>Enterprise-scale experience</li>
                                </ul>
                              </div>
                              <div className="cons">
                                <h6>Cons:</h6>
                                <ul>
                                  <li>Extremely expensive ($200,000-$500,000+)</li>
                                  <li>Junior consultants doing most work</li>
                                  <li>One-size-fits-all approaches</li>
                                  <li>Limited ongoing relationship</li>
                                  <li>Overkill for most growing companies</li>
                                </ul>
                              </div>
                            </div>
                            <div className="best-for">
                              <strong>Best For:</strong> Large enterprises with complex regulatory requirements and big budgets
                            </div>
                          </div>
                        </div>

                        <div className="option-card automation-platforms">
                          <h5>🤖 Automation Platforms (Vanta, Tugboat Logic, etc.)</h5>
                          <div className="option-details">
                            <div className="pros-cons">
                              <div className="pros">
                                <h6>Pros:</h6>
                                <ul>
                                  <li>Automated evidence collection</li>
                                  <li>User-friendly dashboards</li>
                                  <li>Integration with common tools</li>
                                  <li>Templated policies and procedures</li>
                                </ul>
                              </div>
                              <div className="cons">
                                <h6>Cons:</h6>
                                <ul>
                                  <li>Still expensive ($15,000-$50,000+ annually)</li>
                                  <li>Limited strategic guidance</li>
                                  <li>Generic templates don't fit all businesses</li>
                                  <li>No executive-level oversight</li>
                                  <li>You're still on your own for decision-making</li>
                                </ul>
                              </div>
                            </div>
                            <div className="best-for">
                              <strong>Best For:</strong> Companies with existing compliance expertise who need automation tools
                            </div>
                          </div>
                        </div>

                        <div className="option-card freelance-consultants">
                          <h5>👤 Freelance Security Consultants</h5>
                          <div className="option-details">
                            <div className="pros-cons">
                              <div className="pros">
                                <h6>Pros:</h6>
                                <ul>
                                  <li>More affordable than big firms</li>
                                  <li>Direct access to expertise</li>
                                  <li>Flexible engagement models</li>
                                  <li>Personal attention</li>
                                </ul>
                              </div>
                              <div className="cons">
                                <h6>Cons:</h6>
                                <ul>
                                  <li>Quality varies dramatically</li>
                                  <li>Limited availability and scalability</li>
                                  <li>No institutional knowledge backup</li>
                                  <li>Often lack business context</li>
                                  <li>May disappear mid-project</li>
                                </ul>
                              </div>
                            </div>
                            <div className="best-for">
                              <strong>Best For:</strong> Companies with specific, short-term compliance needs
                            </div>
                          </div>
                        </div>

                        <div className="option-card our-approach highlighted">
                          <h5>✅ Our Fractional Executive Approach</h5>
                          <div className="option-details">
                            <div className="pros-cons">
                              <div className="pros">
                                <h6>What Sets Us Apart:</h6>
                                <ul>
                                  <li>Genuine fractional CISO/CIO/Privacy Officer expertise</li>
                                  <li>Board-ready strategic guidance and reporting</li>
                                  <li>Executive independence with direct accountability</li>
                                  <li>Proven 24/7 operational experience</li>
                                  <li>Tools + human expertise hybrid approach</li>
                                  <li>Success guarantees with re-audit fee coverage</li>
                                  <li>60-80% cost savings vs traditional approaches</li>
                                  <li>Scalable from DIY tools to dedicated fractional executives</li>
                                </ul>
                              </div>
                              <div className="cons">
                                <h6>Considerations:</h6>
                                <ul>
                                  <li>Newer approach - less brand recognition</li>
                                  <li>Requires some internal coordination</li>
                                  <li>Best for companies ready to engage strategically</li>
                                </ul>
                              </div>
                            </div>
                            <div className="best-for success">
                              <strong>Best For:</strong> Growing SaaS companies (10-200 employees) who need executive-level compliance expertise without full-time costs
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="why-choose-summary">
                      <h4>Why Our Approach Wins</h4>
                      <div className="winning-points">
                        <div className="winning-point">
                          <h5>🎯 Executive-Level Strategic Focus</h5>
                          <p>Unlike platforms or junior consultants, you get true C-level expertise that understands business strategy, board reporting, and executive accountability. Our fractional model provides the right governance structure.</p>
                        </div>
                        <div className="winning-point">
                          <h5>💰 Exceptional Value Equation</h5>
                          <p>60-80% cost savings compared to traditional approaches while delivering superior outcomes. Our success guarantees ensure you get results, not just effort.</p>
                        </div>
                        <div className="winning-point">
                          <h5>🔧 Practical Implementation Experience</h5>
                          <p>5+ years of 24/7 operational experience supporting SOC 2 compliance gives us deep understanding of what actually works in real-world environments.</p>
                        </div>
                        <div className="winning-point">
                          <h5>🌍 International Perspective</h5>
                          <p>Experience across multiple jurisdictions (Ukraine, UAE, Canada) provides valuable perspective on global compliance requirements and cultural considerations.</p>
                        </div>
                        <div className="winning-point">
                          <h5>📈 Scalable Partnership Model</h5>
                          <p>Grow with us from $199/month DIY tools to $2,500/month dedicated fractional CISO services. No need to switch providers as your needs evolve.</p>
                        </div>
                        <div className="winning-point">
                          <h5>🎓 Education-Focused Approach</h5>
                          <p>We don't just do compliance for you - we teach your team to understand and maintain it. Knowledge transfer is built into every engagement.</p>
                        </div>
                      </div>
                    </div>

                    <div className="decision-framework">
                      <h4>Choose Us If:</h4>
                      <ul className="choose-us-criteria">
                        <li>You're a growing SaaS company (10-200 employees) seeking SOC 2 compliance</li>
                        <li>You need executive-level expertise without full-time executive costs</li>
                        <li>You value strategic guidance over just checkbox compliance</li>
                        <li>You want success guarantees and risk mitigation</li>
                        <li>You prefer collaborative partnerships over vendor relationships</li>
                        <li>You need board-ready reporting and executive accountability</li>
                        <li>You want to build internal capability while getting expert guidance</li>
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeMainTab === 'faq' && (
          <div className="faq-content">
            <div className="content-section">
              {activeFaqTab === 'presoc2costs' && (
                <>
                  <h3>The Hidden Costs of Operating Without a Compliance Framework</h3>
                  <p>Before implementing SOC 2, many organizations underestimate the true cost of operating without a formal compliance framework. These hidden costs often far exceed the investment required for proper SOC 2 implementation.</p>

                  <div className="cost-scenarios">
                    <div className="scenario-section">
                      <h4>Consequences & Costs of No Compliance Framework</h4>
                      <div className="consequence-grid">
                        <div className="consequence-item">
                          <h5>💥 System Outages & Downtime</h5>
                          <div className="cost-details">
                            <p><strong>Average Cost:</strong> $5,600 per minute for mid-size SaaS companies</p>
                            <ul>
                              <li>Revenue loss during outages</li>
                              <li>Customer churn and reputation damage</li>
                              <li>SLA penalty payments</li>
                              <li>Emergency response and recovery costs</li>
                              <li>Regulatory fines for service disruptions</li>
                            </ul>
                            <div className="real-example">
                              <strong>Real Example:</strong> A 4-hour outage can cost a $10M ARR company over $1.3M in direct and indirect costs.
                            </div>
                          </div>
                        </div>

                        <div className="consequence-item">
                          <h5>🔓 Data Breaches & Security Incidents</h5>
                          <div className="cost-details">
                            <p><strong>Average Cost:</strong> $4.45M per breach (IBM 2023 study)</p>
                            <ul>
                              <li>Incident response and investigation ($240,000 average)</li>
                              <li>Customer notification and legal costs ($180,000 average)</li>
                              <li>Regulatory fines and penalties ($500,000+ potential)</li>
                              <li>Lost business and customer churn ($1.6M average)</li>
                              <li>Credit monitoring and identity protection services</li>
                              <li>Litigation and settlement costs</li>
                            </ul>
                            <div className="real-example">
                              <strong>Small Company Reality:</strong> Even a "minor" breach exposing 10,000 customer records can cost $400,000-$800,000.
                            </div>
                          </div>
                        </div>

                        <div className="consequence-item">
                          <h5>🎯 Lack of Governance & Operational Chaos</h5>
                          <div className="cost-details">
                            <p><strong>Hidden Cost:</strong> 20-40% reduction in operational efficiency</p>
                            <ul>
                              <li>Repeated manual processes due to lack of documented procedures</li>
                              <li>Inconsistent security practices across teams</li>
                              <li>Time wasted on ad-hoc security assessments</li>
                              <li>Duplicated efforts and conflicting policies</li>
                              <li>Employee confusion and reduced productivity</li>
                              <li>Technical debt accumulation</li>
                            </ul>
                            <div className="real-example">
                              <strong>Productivity Impact:</strong> A 50-person company can lose $300,000+ annually in productivity due to poor governance.
                            </div>
                          </div>
                        </div>

                        <div className="consequence-item">
                          <h5>⚖️ Regulatory & Legal Exposure</h5>
                          <div className="cost-details">
                            <p><strong>Potential Cost:</strong> $50,000 - $5M+ depending on industry and scope</p>
                            <ul>
                              <li>GDPR fines up to 4% of annual revenue</li>
                              <li>CCPA penalties up to $7,500 per violation</li>
                              <li>Industry-specific compliance violations (HIPAA, PCI, etc.)</li>
                              <li>Audit failures and remediation requirements</li>
                              <li>Legal defense costs for compliance disputes</li>
                              <li>Class action lawsuits from affected customers</li>
                            </ul>
                            <div className="real-example">
                              <strong>Reality Check:</strong> Even healthcare startups face $10,000-$50,000 HIPAA fines for first-time violations.
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="scenario-section">
                      <h4>Costs of Missed Sales Opportunities</h4>
                      <div className="missed-sales-scenarios">
                        <div className="sales-scenario">
                          <h5>Enterprise Deal Loss</h5>
                          <div className="scenario-details">
                            <p><strong>Typical Situation:</strong> Enterprise prospect requires SOC 2 compliance for security approval</p>
                            <div className="cost-breakdown">
                              <div className="cost-item">
                                <span>Lost deal value (average enterprise SaaS)</span>
                                <span>$50,000 - $500,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Sales team time invested (3-6 months)</span>
                                <span>$15,000 - $30,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Marketing costs to generate lead</span>
                                <span>$5,000 - $15,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Opportunity cost (delayed revenue recognition)</span>
                                <span>$25,000 - $100,000</span>
                              </div>
                            </div>
                            <div className="total-impact">
                              <strong>Total Impact per Lost Deal:</strong> $95,000 - $645,000
                            </div>
                          </div>
                        </div>

                        <div className="sales-scenario">
                          <h5>Market Access Limitation</h5>
                          <div className="scenario-details">
                            <p><strong>Typical Situation:</strong> Unable to compete for enterprise RFPs or government contracts</p>
                            <div className="limitation-impacts">
                              <ul>
                                <li>Excluded from 60-80% of enterprise procurement processes</li>
                                <li>Cannot access government/public sector markets</li>
                                <li>Limited to SMB market with lower deal values</li>
                                <li>Longer sales cycles due to security concerns</li>
                                <li>Reduced pricing power due to perceived risk</li>
                              </ul>
                            </div>
                            <div className="market-impact">
                              <strong>Annual Revenue Impact:</strong> 30-50% reduction in addressable market
                            </div>
                          </div>
                        </div>

                        <div className="sales-scenario">
                          <h5>Customer Churn Risk</h5>
                          <div className="scenario-details">
                            <p><strong>Typical Situation:</strong> Existing customers develop compliance requirements</p>
                            <div className="churn-costs">
                              <div className="cost-item">
                                <span>Customer acquisition cost (lost forever)</span>
                                <span>$5,000 - $50,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Lifetime value reduction</span>
                                <span>$25,000 - $500,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Negative word-of-mouth impact</span>
                                <span>$10,000 - $100,000</span>
                              </div>
                            </div>
                            <div className="churn-reality">
                              <strong>Reality:</strong> 20-40% of customers may require compliance within 2-3 years of initial purchase
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="scenario-section">
                      <h4>Security Questionnaire Burden</h4>
                      <div className="questionnaire-costs">
                        <div className="questionnaire-scenario">
                          <h5>Manual Security Assessments</h5>
                          <div className="burden-details">
                            <p>Without SOC 2, every enterprise prospect requires custom security documentation and assessment</p>
                            <div className="questionnaire-breakdown">
                              <div className="burden-item">
                                <h6>Time Investment per Questionnaire</h6>
                                <ul>
                                  <li>CTO/Security Lead: 8-16 hours</li>
                                  <li>Engineering Manager: 4-8 hours</li>
                                  <li>DevOps Engineer: 4-6 hours</li>
                                  <li>Legal Review: 2-4 hours</li>
                                  <li>Sales Engineering: 2-4 hours</li>
                                </ul>
                                <div className="time-cost">
                                  <strong>Total Time Cost:</strong> 20-38 hours × $150/hour average = $3,000-$5,700 per questionnaire
                                </div>
                              </div>

                              <div className="burden-item">
                                <h6>Opportunity Costs</h6>
                                <ul>
                                  <li>Product development delayed</li>
                                  <li>Engineering resources diverted from core features</li>
                                  <li>Leadership time away from strategic initiatives</li>
                                  <li>Inconsistent responses creating compliance gaps</li>
                                </ul>
                              </div>

                              <div className="burden-item">
                                <h6>Annual Volume Impact</h6>
                                <div className="volume-scenarios">
                                  <div className="volume-case">
                                    <strong>Growing SaaS (10-50 employees):</strong>
                                    <p>15-30 questionnaires/year = $45,000-$171,000 in internal costs</p>
                                  </div>
                                  <div className="volume-case">
                                    <strong>Scale-up SaaS (50-200 employees):</strong>
                                    <p>40-80 questionnaires/year = $120,000-$456,000 in internal costs</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="scenario-section solution-section">
                      <h4>The SOC 2 Framework Solution</h4>
                      <div className="solution-benefits">
                        <div className="benefit-comparison">
                          <h5>🛡️ Risk Mitigation</h5>
                          <div className="before-after">
                            <div className="before">
                              <h6>Without SOC 2:</h6>
                              <ul>
                                <li>Reactive incident response</li>
                                <li>Ad-hoc security measures</li>
                                <li>Unknown vulnerabilities</li>
                                <li>Inconsistent data protection</li>
                              </ul>
                            </div>
                            <div className="after">
                              <h6>With SOC 2:</h6>
                              <ul>
                                <li>Proactive risk management</li>
                                <li>Systematic security controls</li>
                                <li>Regular vulnerability assessments</li>
                                <li>Comprehensive data governance</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="benefit-comparison">
                          <h5>💰 Revenue Acceleration</h5>
                          <div className="before-after">
                            <div className="before">
                              <h6>Without SOC 2:</h6>
                              <ul>
                                <li>Lost enterprise deals</li>
                                <li>Extended sales cycles</li>
                                <li>Limited market access</li>
                                <li>Constant questionnaire burden</li>
                              </ul>
                            </div>
                            <div className="after">
                              <h6>With SOC 2:</h6>
                              <ul>
                                <li>Enterprise sales acceleration</li>
                                <li>Shorter security approval cycles</li>
                                <li>Full market access</li>
                                <li>One certification satisfies multiple prospects</li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div className="benefit-comparison">
                          <h5>⚡ Operational Efficiency</h5>
                          <div className="before-after">
                            <div className="before">
                              <h6>Without SOC 2:</h6>
                              <ul>
                                <li>Manual, inconsistent processes</li>
                                <li>Reactive security responses</li>
                                <li>Duplicated documentation efforts</li>
                                <li>Team confusion and inefficiency</li>
                              </ul>
                            </div>
                            <div className="after">
                              <h6>With SOC 2:</h6>
                              <ul>
                                <li>Documented, repeatable processes</li>
                                <li>Proactive monitoring and controls</li>
                                <li>Centralized compliance documentation</li>
                                <li>Clear roles and responsibilities</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="roi-summary">
                        <h5>ROI Reality Check</h5>
                        <div className="roi-calculation">
                          <div className="cost-side">
                            <h6>Typical SOC 2 Investment:</h6>
                            <p>$25,000 - $75,000 annually (including our services)</p>
                          </div>
                          <div className="savings-side">
                            <h6>Typical Annual Savings/Benefits:</h6>
                            <ul>
                              <li>Avoided breach costs: $500,000 - $4,000,000</li>
                              <li>Revenue acceleration: $200,000 - $2,000,000</li>
                              <li>Operational efficiency: $100,000 - $500,000</li>
                              <li>Questionnaire time savings: $50,000 - $400,000</li>
                            </ul>
                            <div className="total-benefit">
                              <strong>Total Annual Benefit: $850,000 - $6,900,000</strong>
                            </div>
                          </div>
                        </div>
                        <div className="roi-conclusion">
                          <strong>Typical ROI: 10x to 90x return on investment in first year alone</strong>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="action-cta">
                    <h4>Ready to Eliminate These Hidden Costs?</h4>
                    <p>Don't let the absence of SOC 2 compliance drain your resources and limit your growth. Our fractional executive services help you implement comprehensive compliance frameworks efficiently and cost-effectively.</p>
                    <button className="contact-btn" onClick={() => setShowConsultationForm(true)}>
                      Calculate Your Hidden Costs
                    </button>
                  </div>
                </>
              )}

              {activeFaqTab === 'soc2criteria' && (
                <>
                  <h3>SOC 2 Trust Criteria - Detailed Reference</h3>
                  <p>Comprehensive details on SOC 2 Trust Services Criteria, including specific points of focus, implementation guidance, and examples.</p>

                  <div className="criteria-overview-faq">
                    <div className="criteria-intro">
                      <h4>SOC 2 Trust Services Overview</h4>
                      <p>SOC 2 (Service Organization Control 2) is an auditing framework that evaluates the controls relevant to security, availability, processing integrity, confidentiality, and privacy. It's designed specifically for service providers that store customer data in the cloud.</p>
                      
                      <div className="criteria-categories">
                        <div className="category-card security">
                          <h5>Security (Common Criteria)</h5>
                          <p><strong>Required for all SOC 2 audits.</strong> Information and systems are protected against unauthorized access, unauthorized disclosure of information, and damage to systems.</p>
                          <div className="category-details">
                            <strong>Key Focus Areas:</strong>
                            <ul>
                              <li>Control Environment & Governance</li>
                              <li>Communication & Information Systems</li>
                              <li>Risk Assessment & Management</li>
                              <li>Control Activities & Monitoring</li>
                              <li>Logical & Physical Access Controls</li>
                              <li>System Operations & Change Management</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="category-card availability">
                          <h5>Availability</h5>
                          <p><strong>Optional.</strong> Information or systems are available for operation and use as committed or agreed upon.</p>
                          <div className="category-details">
                            <strong>Key Focus Areas:</strong>
                            <ul>
                              <li>Capacity Management & Performance Monitoring</li>
                              <li>System Backup & Recovery</li>
                              <li>Environmental Protection & Physical Safeguards</li>
                              <li>Network Operations & Incident Response</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="category-card processing">
                          <h5>Processing Integrity</h5>
                          <p><strong>Optional.</strong> System processing is complete, valid, accurate, timely, and authorized to meet the entity's objectives.</p>
                          <div className="category-details">
                            <strong>Key Focus Areas:</strong>
                            <ul>
                              <li>Data Input & Processing Controls</li>
                              <li>System Interfaces & Integration</li>
                              <li>Data Quality & Accuracy</li>
                              <li>Error Detection & Handling</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="category-card confidentiality">
                          <h5>Confidentiality</h5>
                          <p><strong>Optional.</strong> Information designated as confidential is protected as committed or agreed upon.</p>
                          <div className="category-details">
                            <strong>Key Focus Areas:</strong>
                            <ul>
                              <li>Data Classification & Handling</li>
                              <li>Access Controls for Confidential Data</li>
                              <li>Encryption & Data Protection</li>
                              <li>Secure Data Disposal</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="category-card privacy">
                          <h5>Privacy</h5>
                          <p><strong>Optional.</strong> Personal information is collected, used, retained, disclosed, and disposed of in conformity with commitments and privacy principles.</p>
                          <div className="category-details">
                            <strong>Key Focus Areas:</strong>
                            <ul>
                              <li>Privacy Notice & Consent Management</li>
                              <li>Data Collection & Use Limitation</li>
                              <li>Individual Rights & Access</li>
                              <li>Data Quality & Retention</li>
                              <li>Disclosure & Cross-Border Transfers</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Add the criteria tabs within FAQ */}
                  <div className="criteria-tabs-faq">
                    {criteriaTabs.map(tab => (
                      <button
                        key={tab.code}
                        className={`criteria-tab ${activeTab === tab.code ? 'active' : ''}`}
                        onClick={() => handleCriteriaTabClick(tab.code)}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </div>

                  <div className="tab-description-faq">
                    <h4>{getCurrentCriteriaTab()?.name}</h4>
                    <p>{getCurrentCriteriaTab()?.description}</p>
                  </div>

                  {/* Move the detailed criteria content here */}
                  <Suspense fallback={
                    <div className="loading-container">
                      <div className="loading-spinner"></div>
                      <div className="loading-text">Loading SOC 2 criteria details...</div>
                    </div>
                  }>
                    <CriteriaList 
                      criteria={filteredCriteria}
                      loading={false}
                      activeTab={activeTab}
                    />
                  </Suspense>

                  <div className="faq-section" style={{marginTop: '40px', borderTop: '2px solid rgba(255,255,255,0.1)', paddingTop: '30px'}}>
                    <h4>Common Questions About SOC 2 Trust Criteria</h4>
                    <div className="faq-item">
                      <h5>What are SOC 2 Trust Services Criteria?</h5>
                      <p>SOC 2 Trust Services Criteria are five principles developed by the AICPA for evaluating controls at service organizations: Security (Common Criteria), Availability, Processing Integrity, Confidentiality, and Privacy. Security is required for all SOC 2 audits, while the others are optional based on your business commitments.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What's the difference between SOC 2 Type I and Type II?</h5>
                      <p>SOC 2 Type I evaluates the design and implementation of controls at a specific point in time. SOC 2 Type II evaluates the operating effectiveness of controls over a period of time (minimum 6 months). Most organizations pursue Type II for ongoing compliance demonstration.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Which trust services criteria should we implement?</h5>
                      <p>Security (Common Criteria) is required for all SOC 2 reports. Additional criteria depend on your business commitments: Availability if you promise uptime, Processing Integrity if you guarantee accurate processing, Confidentiality if you handle confidential data, and Privacy if you process personal information.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How can your fractional executive services help with SOC 2?</h5>
                      <p>Our services provide fractional CISO, CIO, and Privacy Officer expertise without full-time costs. Starting at $199/month for DIY tools up to $2,500/month for dedicated fractional CISO services, we offer the right level of executive guidance for your stage and budget with success guarantees.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How do the Common Criteria (CC) relate to other frameworks?</h5>
                      <p>The Common Criteria are based on the COSO Internal Control framework, adapted for IT and service organizations. They provide foundational security controls that support other frameworks like ISO 27001, NIST, and industry-specific requirements.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What's the relationship between SOC 2 and GDPR/privacy laws?</h5>
                      <p>SOC 2 Privacy criteria can support GDPR compliance but don't replace it. SOC 2 focuses on controls and processes, while GDPR focuses on legal rights and obligations. Many organizations use SOC 2 as part of their broader privacy compliance strategy.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How often do we need to be audited?</h5>
                      <p>Most organizations pursue annual SOC 2 Type II audits to maintain current certification status. Some may choose more frequent audits based on customer requirements or business needs.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Can we scope out certain systems or processes?</h5>
                      <p>Yes, SOC 2 audits can be scoped to specific systems, services, or business units. The scope should align with what you've committed to customers and what's material to your service delivery.</p>
                    </div>
                  </div>
                </>
              )}

              {activeFaqTab === 'type1audit' && (
                <>
                  <h3>SOC 2 Type I Audit</h3>
                  <p>Understanding the SOC 2 Type I audit process, requirements, and what to expect during your first audit.</p>

                  <div className="audit-overview">
                    <div className="audit-definition">
                      <h4>What is a SOC 2 Type I Audit?</h4>
                      <p>A SOC 2 Type I audit is a point-in-time assessment that evaluates the <strong>design and implementation</strong> of your organization's controls related to the Trust Services Criteria. It examines whether your controls are properly designed and have been implemented as of a specific date (the "as of" date).</p>
                    </div>

                    <div className="audit-characteristics">
                      <h4>Key Characteristics of Type I Audits</h4>
                      <div className="characteristics-grid">
                        <div className="characteristic-item">
                          <h5>Point-in-Time Assessment</h5>
                          <p>Evaluates controls as of a specific date, not over a period of time. The auditor examines your control environment at one moment.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>Design Effectiveness</h5>
                          <p>Reviews whether controls are designed appropriately to meet the criteria and achieve control objectives.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>Implementation Testing</h5>
                          <p>Verifies that controls have been put into operation and are being used as designed.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>No Operating Effectiveness</h5>
                          <p>Does not test whether controls operated effectively over time - that's reserved for Type II audits.</p>
                        </div>
                      </div>
                    </div>

                    <div className="audit-process">
                      <h4>Type I Audit Process</h4>
                      <div className="process-steps">
                        <div className="process-step">
                          <h5>1. Pre-Audit Planning (2-4 weeks)</h5>
                          <ul>
                            <li>Define audit scope and applicable criteria</li>
                            <li>Prepare system description and control documentation</li>
                            <li>Schedule audit activities and resources</li>
                            <li>Complete readiness assessment</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>2. Documentation Review (1-2 weeks)</h5>
                          <ul>
                            <li>Auditor reviews policies, procedures, and control documentation</li>
                            <li>System description validation</li>
                            <li>Control design evaluation</li>
                            <li>Identification of areas requiring additional evidence</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>3. Testing and Interviews (1-2 weeks)</h5>
                          <ul>
                            <li>Control implementation testing</li>
                            <li>Management and staff interviews</li>
                            <li>Walkthrough of key processes</li>
                            <li>Evidence collection and validation</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>4. Report Issuance (2-3 weeks)</h5>
                          <ul>
                            <li>Draft report review with management</li>
                            <li>Management response to findings</li>
                            <li>Final report completion</li>
                            <li>Report distribution to stakeholders</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="audit-deliverables">
                      <h4>Type I Audit Deliverables</h4>
                      <div className="deliverables-list">
                        <div className="deliverable-item">
                          <h5>Management's System Description</h5>
                          <p>Detailed description of the service organization's system, including infrastructure, software, people, procedures, and data.</p>
                        </div>
                        <div className="deliverable-item">
                          <h5>Management's Assertion</h5>
                          <p>Written statement from management about the fairness of the system description and suitability of control design and implementation.</p>
                        </div>
                        <div className="deliverable-item">
                          <h5>Independent Auditor's Report</h5>
                          <p>Auditor's opinion on the fairness of the system description and suitability of control design and implementation.</p>
                        </div>
                        <div className="deliverable-item">
                          <h5>Control Matrix</h5>
                          <p>Detailed mapping of controls to Trust Services Criteria with auditor testing results and conclusions.</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="faq-section">
                    <h4>Common Questions About Type I Audits</h4>
                    <div className="faq-item">
                      <h5>How long does a Type I audit take?</h5>
                      <p>Typically 6-10 weeks from start to final report, including 2-3 weeks of active auditing. Timeline varies based on organization size, complexity, and readiness.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What does a Type I audit cost?</h5>
                      <p>Type I audits typically cost $15,000-$35,000 for small to mid-size organizations, depending on scope, complexity, and auditor selection.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Is Type I sufficient for most customers?</h5>
                      <p>Type I can be useful for initial compliance demonstration, but most enterprise customers and regulatory requirements expect Type II audits for ongoing assurance.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Can we go straight to Type II?</h5>
                      <p>Yes, many organizations skip Type I and go directly to Type II, especially if they're confident in their control maturity and have adequate preparation time.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What happens if we fail a Type I audit?</h5>
                      <p>Failures typically result in qualified opinions or disclaimers. You'll need to address deficiencies and potentially undergo a re-audit before proceeding to Type II.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How do we prepare for a Type I audit?</h5>
                      <p>Focus on documenting policies and procedures, implementing controls, training staff, and conducting internal assessments. Our preparation services help ensure readiness.</p>
                    </div>
                  </div>
                </>
              )}

              {activeFaqTab === 'type2audit' && (
                <>
                  <h3>SOC 2 Type II Audit</h3>
                  <p>Understanding the SOC 2 Type II audit process, requirements, and what makes it the gold standard for SOC 2 compliance.</p>

                  <div className="audit-overview">
                    <div className="audit-definition">
                      <h4>What is a SOC 2 Type II Audit?</h4>
                      <p>A SOC 2 Type II audit evaluates both the <strong>design and operating effectiveness</strong> of your organization's controls over a period of time (minimum 6 months). It not only confirms that controls are properly designed and implemented, but also that they operated effectively throughout the audit period.</p>
                    </div>

                    <div className="audit-characteristics">
                      <h4>Key Characteristics of Type II Audits</h4>
                      <div className="characteristics-grid">
                        <div className="characteristic-item">
                          <h5>Period-of-Time Assessment</h5>
                          <p>Evaluates controls over a minimum 6-month period, providing evidence of consistent operation over time.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>Operating Effectiveness</h5>
                          <p>Tests whether controls operated effectively throughout the audit period, not just at a point in time.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>Exception Reporting</h5>
                          <p>Identifies and reports instances where controls did not operate as designed during the audit period.</p>
                        </div>
                        <div className="characteristic-item">
                          <h5>Greater Assurance</h5>
                          <p>Provides higher level of assurance to stakeholders about ongoing control effectiveness.</p>
                        </div>
                      </div>
                    </div>

                    <div className="audit-requirements">
                      <h4>Type II Audit Requirements</h4>
                      <div className="requirements-grid">
                        <div className="requirement-item">
                          <h5>Minimum Audit Period</h5>
                          <p>At least 6 months of controls operation, though 12 months is preferred for annual reporting cycles.</p>
                        </div>
                        <div className="requirement-item">
                          <h5>Continuous Operation</h5>
                          <p>Controls must be operating throughout the entire audit period - gaps in operation can lead to qualified opinions.</p>
                        </div>
                        <div className="requirement-item">
                          <h5>Evidence Documentation</h5>
                          <p>Comprehensive evidence of control operation, including logs, reports, approvals, and monitoring activities.</p>
                        </div>
                        <div className="requirement-item">
                          <h5>Management Monitoring</h5>
                          <p>Evidence that management monitored control effectiveness and addressed deficiencies during the period.</p>
                        </div>
                      </div>
                    </div>

                    <div className="audit-process">
                      <h4>Type II Audit Process</h4>
                      <div className="process-steps">
                        <div className="process-step">
                          <h5>1. Pre-Audit Planning (4-6 weeks)</h5>
                          <ul>
                            <li>Define 6+ month audit period and scope</li>
                            <li>Prepare comprehensive system description</li>
                            <li>Document all policies, procedures, and controls</li>
                            <li>Conduct internal readiness assessment</li>
                            <li>Organize evidence files by control and time period</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>2. Interim Testing (2-3 weeks)</h5>
                          <ul>
                            <li>Initial control design and implementation testing</li>
                            <li>Sample testing of control operation for early months</li>
                            <li>Identification of potential exceptions or issues</li>
                            <li>Remediation of identified deficiencies</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>3. Detailed Testing (3-4 weeks)</h5>
                          <ul>
                            <li>Comprehensive testing across entire audit period</li>
                            <li>Statistical sampling of control evidence</li>
                            <li>Management and staff interviews</li>
                            <li>System walkthroughs and observations</li>
                            <li>Exception identification and evaluation</li>
                          </ul>
                        </div>
                        <div className="process-step">
                          <h5>4. Report Finalization (3-4 weeks)</h5>
                          <ul>
                            <li>Draft report review with management</li>
                            <li>Management responses to findings and exceptions</li>
                            <li>Final testing and evidence review</li>
                            <li>Report completion and distribution</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="testing-approach">
                      <h4>Type II Testing Approach</h4>
                      <div className="testing-methods">
                        <div className="testing-method">
                          <h5>Statistical Sampling</h5>
                          <p>Auditors select representative samples of control evidence throughout the audit period using statistical methods to ensure adequate coverage.</p>
                        </div>
                        <div className="testing-method">
                          <h5>Risk-Based Testing</h5>
                          <p>Higher-risk controls receive more extensive testing, while lower-risk controls may have smaller sample sizes.</p>
                        </div>
                        <div className="testing-method">
                          <h5>Continuous Monitoring Review</h5>
                          <p>Evaluation of automated controls and monitoring systems that operate continuously throughout the period.</p>
                        </div>
                        <div className="testing-method">
                          <h5>Exception Analysis</h5>
                          <p>Detailed investigation of any instances where controls did not operate as expected, including root cause analysis.</p>
                        </div>
                      </div>
                    </div>

                    <div className="common-exceptions">
                      <h4>Common Type II Exceptions</h4>
                      <div className="exceptions-list">
                        <div className="exception-category">
                          <h5>Access Management Exceptions</h5>
                          <ul>
                            <li>Delayed removal of terminated user access</li>
                            <li>Inappropriate privilege assignments</li>
                            <li>Missing periodic access reviews</li>
                            <li>Weak password compliance</li>
                          </ul>
                        </div>
                        <div className="exception-category">
                          <h5>Change Management Exceptions</h5>
                          <ul>
                            <li>Unauthorized changes to production systems</li>
                            <li>Missing change approval documentation</li>
                            <li>Inadequate testing before deployment</li>
                            <li>Emergency changes without proper authorization</li>
                          </ul>
                        </div>
                        <div className="exception-category">
                          <h5>Monitoring and Logging Exceptions</h5>
                          <ul>
                            <li>Gaps in security event monitoring</li>
                            <li>Log retention policy violations</li>
                            <li>Delayed incident response</li>
                            <li>Incomplete vulnerability assessments</li>
                          </ul>
                        </div>
                        <div className="exception-category">
                          <h5>Operational Exceptions</h5>
                          <ul>
                            <li>Backup and recovery failures</li>
                            <li>Missing business continuity testing</li>
                            <li>Inadequate vendor management</li>
                            <li>Training completion gaps</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="faq-section">
                    <h4>Common Questions About Type II Audits</h4>
                    <div className="faq-item">
                      <h5>How long does a Type II audit take?</h5>
                      <p>Typically 12-16 weeks from start to final report, including 6+ months of controls operation before the audit begins. Active auditing takes 4-6 weeks.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What does a Type II audit cost?</h5>
                      <p>Type II audits typically cost $25,000-$75,000+ depending on organization size, scope complexity, and number of trust service categories included.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How do we prepare for the 6-month audit period?</h5>
                      <p>Implement all controls, document procedures, train staff, and begin consistent operation. Our services help ensure controls operate effectively from day one.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What happens if we have exceptions?</h5>
                      <p>Minor exceptions may not affect the overall opinion if they're not material. Significant exceptions can result in qualified opinions or additional remediation requirements.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Can we remediate issues during the audit period?</h5>
                      <p>Yes, but remediation must occur within the audit period and be sustained. Late-period fixes may not be sufficient to overcome earlier exceptions.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How often should we get Type II audits?</h5>
                      <p>Most organizations pursue annual Type II audits to maintain current certification status and meet ongoing customer and regulatory requirements.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What's the difference in customer acceptance?</h5>
                      <p>Type II reports are generally preferred by enterprise customers, investors, and regulators as they provide stronger assurance about ongoing control effectiveness.</p>
                    </div>
                  </div>
                </>
              )}

              {activeFaqTab === 'general' && (
                <>
                  <h3>General FAQ</h3>
                  <p>Frequently asked questions about our SOC 2 preparation services and process.</p>

                  <div className="faq-section">
                    <div className="faq-item">
                      <h5>How long does SOC 2 preparation take?</h5>
                      <p>Typically 3-6 months for initial implementation, plus 6-12 months of controls operation before the audit. Timeline depends on your current state, company size, and complexity of systems. Our fractional executive guidance accelerates this process significantly.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What's the total cost of SOC 2 compliance?</h5>
                      <p>Our services range from $199/month (DIY Toolkit) to $2,500/month (Fractional CISO Elite) for monthly plans, or $2,500-$25,000/year for annual plans. This is typically 60-80% less than hiring full-time staff or traditional consulting approaches.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Do you guarantee audit success?</h5>
                      <p>Yes! Our Guided Essentials includes a Type I pass guarantee. Co-Pilot Plus and Fractional CISO Elite include re-audit fee coverage up to $4,000-$10,000. Annual plans include even stronger guarantees with unlimited additional prep coaching.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What makes your fractional executive approach different?</h5>
                      <p>You get dedicated CISO, CIO, or Privacy Officer expertise without the $180,000-$250,000 salary. Our fractional executives provide strategic guidance, board-ready reporting, and executive presence while maintaining the independence required for effective security governance.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Can you help with other compliance frameworks?</h5>
                      <p>Yes, our Co-Pilot Plus and Fractional CISO Elite packages include support for additional frameworks like ISO 27001, HIPAA, PCI DSS, and others. All annual plans include multi-framework support and customized policy development.</p>
                    </div>
                    <div className="faq-item">
                      <h5>Do you work with companies outside the US?</h5>
                      <p>Yes, we work with companies globally. SOC 2 is increasingly recognized internationally, and many global companies pursue SOC 2 certification to serve US customers or meet international standards.</p>
                    </div>
                    <div className="faq-item">
                      <h5>What if we're not ready for a full SOC 2 audit?</h5>
                      <p>Our DIY Toolkit ($199/month) and Starter Annual ($2,500/year) are perfect for companies in early preparation phases. We help you understand requirements, assess your current state, and build toward audit readiness at your own pace.</p>
                    </div>
                    <div className="faq-item">
                      <h5>How do you maintain independence as fractional executives?</h5>
                      <p>We report directly to company leadership/board level, maintaining the independence required for effective security governance. This fractional model provides the right accountability structure without conflicts of interest while delivering executive-level expertise.</p>
                    </div>
                  </div>
                </>
              )}

              {activeFaqTab === 'budgeting' && (
                <>
                  <h3>SOC 2 Budgeting Guide</h3>
                  <p>Detailed cost breakdowns for SOC 2 preparation scenarios, comparing DIY approaches with our fractional executive services.</p>

                  <div className="budgeting-scenarios">
                    <div className="scenario-comparison">
                      <h4>Scenario 1: Small SaaS Company (10-25 employees)</h4>
                      <div className="cost-comparison-grid">
                        <div className="cost-scenario without-service">
                          <h5>Without Our Service (DIY Approach)</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Personnel Costs (Annual)</h6>
                              <div className="cost-item">
                                <span>Part-time Security Consultant (20% FTE)</span>
                                <span>$40,000</span>
                              </div>
                              <div className="cost-item">
                                <span>CTO/Founder time (30% for 6 months)</span>
                                <span>$45,000</span>
                              </div>
                              <div className="cost-item">
                                <span>IT Manager time (40% for 8 months)</span>
                                <span>$35,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Developer time (10% ongoing)</span>
                                <span>$12,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Tools & Platforms</h6>
                              <div className="cost-item">
                                <span>Vanta or similar platform</span>
                                <span>$18,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Security tools (SIEM, monitoring)</span>
                                <span>$15,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Audit firm</span>
                                <span>$25,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Additional Costs</h6>
                              <div className="cost-item">
                                <span>Legal review</span>
                                <span>$8,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Training & certifications</span>
                                <span>$5,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Re-audit risk (30% chance)</span>
                                <span>$7,500</span>
                              </div>
                            </div>
                            <div className="total-cost">
                              <strong>Total First Year: $210,500</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $95,000</span>
                            </div>
                          </div>
                        </div>

                        <div className="cost-scenario with-service">
                          <h5>With Our Professional Service</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Our Service</h6>
                              <div className="cost-item">
                                <span>Professional Annual Package</span>
                                <span>$5,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Reduced Internal Costs</h6>
                              <div className="cost-item">
                                <span>CTO/Founder time (10% for 3 months)</span>
                                <span>$11,250</span>
                              </div>
                              <div className="cost-item">
                                <span>IT Manager time (20% for 4 months)</span>
                                <span>$13,333</span>
                              </div>
                              <div className="cost-item">
                                <span>Developer time (5% ongoing)</span>
                                <span>$6,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Required Tools (Reduced)</h6>
                              <div className="cost-item">
                                <span>Basic security tools</span>
                                <span>$8,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Audit firm</span>
                                <span>$22,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Eliminated Costs</h6>
                              <div className="cost-item eliminated">
                                <span>External consultant</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Compliance platform</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Re-audit risk (covered by guarantee)</span>
                                <span>$0</span>
                              </div>
                            </div>
                            <div className="total-cost savings">
                              <strong>Total First Year: $65,583</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $35,000</span>
                            </div>
                            <div className="savings-highlight">
                              <strong>First Year Savings: $144,917 (69%)</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="scenario-comparison">
                      <h4>Scenario 2: Mid-Size SaaS Company (50-100 employees)</h4>
                      <div className="cost-comparison-grid">
                        <div className="cost-scenario without-service">
                          <h5>Without Our Service (Traditional Approach)</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Personnel Costs (Annual)</h6>
                              <div className="cost-item">
                                <span>Full-time CISO</span>
                                <span>$180,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Compliance Manager</span>
                                <span>$95,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Security Engineer (50% FTE)</span>
                                <span>$65,000</span>
                              </div>
                              <div className="cost-item">
                                <span>IT Manager time (30% for 6 months)</span>
                                <span>$27,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Developer team time (15% ongoing)</span>
                                <span>$45,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Tools & Platforms</h6>
                              <div className="cost-item">
                                <span>Enterprise compliance platform (Tugboat Logic)</span>
                                <span>$36,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Advanced security tools</span>
                                <span>$45,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Big 4 audit firm</span>
                                <span>$45,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Additional Costs</h6>
                              <div className="cost-item">
                                <span>Legal & consulting</span>
                                <span>$25,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Training programs</span>
                                <span>$15,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Re-audit risk (20% chance)</span>
                                <span>$9,000</span>
                              </div>
                            </div>
                            <div className="total-cost">
                              <strong>Total First Year: $587,000</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $512,000</span>
                            </div>
                          </div>
                        </div>

                        <div className="cost-scenario with-service">
                          <h5>With Our Fractional CISO Elite Service</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Our Service</h6>
                              <div className="cost-item">
                                <span>Fractional CISO Elite Package</span>
                                <span>$30,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Reduced Personnel Costs</h6>
                              <div className="cost-item">
                                <span>Part-time Compliance Coordinator</span>
                                <span>$55,000</span>
                              </div>
                              <div className="cost-item">
                                <span>IT Manager time (15% for 4 months)</span>
                                <span>$13,500</span>
                              </div>
                              <div className="cost-item">
                                <span>Developer team time (8% ongoing)</span>
                                <span>$24,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Required Tools (Reduced)</h6>
                              <div className="cost-item">
                                <span>Mid-tier security tools</span>
                                <span>$25,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Audit firm</span>
                                <span>$38,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Eliminated Costs</h6>
                              <div className="cost-item eliminated">
                                <span>Full-time CISO</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Compliance platform</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>External consultants</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Re-audit risk (covered by guarantee)</span>
                                <span>$0</span>
                              </div>
                            </div>
                            <div className="total-cost savings">
                              <strong>Total First Year: $185,500</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $142,000</span>
                            </div>
                            <div className="savings-highlight">
                              <strong>First Year Savings: $401,500 (68%)</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="scenario-comparison">
                      <h4>Scenario 3: Enterprise Company (200+ employees)</h4>
                      <div className="cost-comparison-grid">
                        <div className="cost-scenario without-service">
                          <h5>Without Our Service (Full In-House Team)</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Personnel Costs (Annual)</h6>
                              <div className="cost-item">
                                <span>Chief Information Security Officer</span>
                                <span>$250,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Compliance Manager</span>
                                <span>$120,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Security Engineers (2 FTE)</span>
                                <span>$260,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Risk & Audit Manager</span>
                                <span>$110,000</span>
                              </div>
                              <div className="cost-item">
                                <span>DevOps/Infrastructure team (25%)</span>
                                <span>$75,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Tools & Platforms</h6>
                              <div className="cost-item">
                                <span>Enterprise GRC platform</span>
                                <span>$120,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Comprehensive security stack</span>
                                <span>$150,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Big 4 audit firm (multiple frameworks)</span>
                                <span>$85,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Additional Costs</h6>
                              <div className="cost-item">
                                <span>External legal & consulting</span>
                                <span>$50,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Training & certifications</span>
                                <span>$30,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Penetration testing</span>
                                <span>$25,000</span>
                              </div>
                            </div>
                            <div className="total-cost">
                              <strong>Total First Year: $1,275,000</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $1,170,000</span>
                            </div>
                          </div>
                        </div>

                        <div className="cost-scenario with-service">
                          <h5>With Our Premium Plus Service + Selective Hiring</h5>
                          <div className="cost-breakdown">
                            <div className="cost-category">
                              <h6>Our Service</h6>
                              <div className="cost-item">
                                <span>Premium Plus Annual Package</span>
                                <span>$25,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Strategic Internal Hires</h6>
                              <div className="cost-item">
                                <span>Compliance Manager (we help recruit)</span>
                                <span>$120,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Security Engineer (1 FTE)</span>
                                <span>$130,000</span>
                              </div>
                              <div className="cost-item">
                                <span>DevOps/Infrastructure team (15%)</span>
                                <span>$45,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Required Tools (Optimized)</h6>
                              <div className="cost-item">
                                <span>Curated security tools</span>
                                <span>$85,000</span>
                              </div>
                              <div className="cost-item">
                                <span>Audit firm</span>
                                <span>$75,000</span>
                              </div>
                            </div>
                            <div className="cost-category">
                              <h6>Eliminated Roles</h6>
                              <div className="cost-item eliminated">
                                <span>Full-time CISO (we provide fractional)</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Risk & Audit Manager (included in service)</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Additional Security Engineer</span>
                                <span>$0</span>
                              </div>
                              <div className="cost-item eliminated">
                                <span>Enterprise GRC platform</span>
                                <span>$0</span>
                              </div>
                            </div>
                            <div className="total-cost savings">
                              <strong>Total First Year: $480,000</strong>
                            </div>
                            <div className="ongoing-cost">
                              <span>Ongoing Annual: $455,000</span>
                            </div>
                            <div className="savings-highlight">
                              <strong>First Year Savings: $795,000 (62%)</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="budgeting-insights">
                      <h4>Key Cost Insights</h4>
                      <div className="insights-grid">
                        <div className="insight-card">
                          <h5>Hidden Costs of DIY Approach</h5>
                          <ul>
                            <li>Opportunity cost of executive time diverted from core business</li>
                            <li>Learning curve and mistakes that delay certification</li>
                            <li>Over-purchasing of tools and platforms</li>
                            <li>Re-audit fees when initial attempts fail</li>
                            <li>Ongoing maintenance without strategic oversight</li>
                          </ul>
                        </div>
                        <div className="insight-card">
                          <h5>ROI of Fractional Executive Model</h5>
                          <ul>
                            <li>Access to C-level expertise without C-level salary</li>
                            <li>Faster time to compliance through proven methodologies</li>
                            <li>Right-sized tool selection and implementation</li>
                            <li>Success guarantees that eliminate re-audit risk</li>
                            <li>Ongoing strategic guidance for evolving requirements</li>
                          </ul>
                        </div>
                        <div className="insight-card">
                          <h5>Break-Even Analysis</h5>
                          <ul>
                            <li>Small companies: Break even in 3-4 months</li>
                            <li>Mid-size companies: Break even in 2-3 months</li>
                            <li>Enterprise companies: Break even in 1-2 months</li>
                            <li>Savings compound over time with ongoing efficiency</li>
                            <li>Risk mitigation value often exceeds direct cost savings</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="cost-calculator-cta">
                      <h4>Get Your Custom Cost Analysis</h4>
                      <p>Every organization's SOC 2 journey is unique. Contact us for a personalized cost breakdown based on your specific situation, team size, and compliance requirements.</p>
                      <button className="contact-btn" onClick={() => setShowConsultationForm(true)}>
                        Get Custom Cost Analysis
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {activeMainTab === 'contact' && (
          <div className="contact-content">
            <div className="content-section">
              {activeContactTab === 'consultations' && (
                <>
                  <h3>Get in Touch</h3>
                  <p>Ready to start your SOC 2 journey or need expert guidance? We're here to help.</p>

                  <div className="contact-grid">
                    <div className="contact-method">
                      <h4>Schedule a Consultation</h4>
                      <p>Book a free 30-minute consultation to discuss your SOC 2 needs and compliance timeline.</p>
                      <button className="contact-btn" onClick={() => setShowConsultationForm(true)}>Schedule Consultation</button>
                    </div>

                    <div className="contact-method">
                      <h4>Email Us</h4>
                      <p>Send us your questions and we'll get back to you within 24 hours.</p>
                      <a href="mailto:info@soc2experts.com" className="contact-btn">info@soc2experts.com</a>
                    </div>

                    <div className="contact-method">
                      <h4>Phone Support</h4>
                      <p>Speak directly with our SOC 2 experts during business hours (9 AM - 6 PM EST).</p>
                      <a href="tel:+1-415-906-6005" className="contact-btn">+1 (415) 906-6005</a>
                    </div>
                  </div>
                </>
              )}

              {activeContactTab === 'careers' && (
                <>
                  <h3>Join Our Team</h3>
                  <p>Help us make SOC 2 compliance accessible and affordable for growing businesses. We're building a team of experts who share our mission.</p>

                  <div className="careers-section">
                    <div className="company-culture">
                      <h4>Why Work With Us</h4>
                      <div className="culture-grid">
                        <div className="culture-item">
                          <h5>🌍 Remote-First Culture</h5>
                          <p>Work from anywhere with flexible hours. We value results over location and trust our team to deliver excellence.</p>
                        </div>
                        <div className="culture-item">
                          <h5>📈 Growing Market</h5>
                          <p>SOC 2 compliance is experiencing explosive growth. Join us in building the future of fractional compliance services.</p>
                        </div>
                        <div className="culture-item">
                          <h5>🎯 Mission-Driven</h5>
                          <p>Help small and medium businesses achieve enterprise-level security without enterprise-level costs.</p>
                        </div>
                        <div className="culture-item">
                          <h5>🧠 Continuous Learning</h5>
                          <p>Stay current with evolving compliance frameworks and security best practices. We invest in your professional development.</p>
                        </div>
                      </div>
                    </div>

                    <div className="open-positions">
                      <h4>Current Openings</h4>
                      
                      <div className="job-listing">
                        <h5>Senior SOC 2 Consultant (Remote)</h5>
                        <div className="job-details">
                          <p><strong>Location:</strong> Remote (North America preferred)</p>
                          <p><strong>Experience:</strong> 5+ years in SOC 2 auditing or compliance</p>
                          <p><strong>Requirements:</strong></p>
                          <ul>
                            <li>CPA, CIA, CISA, or equivalent certification</li>
                            <li>Experience conducting SOC 2 Type I and Type II audits</li>
                            <li>Strong understanding of AICPA Trust Services Criteria</li>
                            <li>Excellent communication and client-facing skills</li>
                            <li>Experience with technology companies and SaaS platforms</li>
                          </ul>
                        </div>
                      </div>

                      <div className="job-listing">
                        <h5>Fractional CISO (Contract)</h5>
                        <div className="job-details">
                          <p><strong>Location:</strong> Remote</p>
                          <p><strong>Experience:</strong> 8+ years in information security leadership</p>
                          <p><strong>Requirements:</strong></p>
                          <ul>
                            <li>Previous CISO or senior security leadership experience</li>
                            <li>Strong background in compliance frameworks (SOC 2, ISO 27001, NIST)</li>
                            <li>Experience with board-level reporting and communication</li>
                            <li>Ability to work with multiple clients in fractional capacity</li>
                            <li>Security certifications (CISSP, CISM, etc.) preferred</li>
                          </ul>
                        </div>
                      </div>

                      <div className="job-listing">
                        <h5>Privacy Officer / Consultant (Part-time)</h5>
                        <div className="job-details">
                          <p><strong>Location:</strong> Remote</p>
                          <p><strong>Experience:</strong> 3+ years in privacy compliance</p>
                          <p><strong>Requirements:</strong></p>
                          <ul>
                            <li>Strong knowledge of GDPR, CCPA, and privacy frameworks</li>
                            <li>Experience implementing privacy programs</li>
                            <li>Law degree or privacy certification (IAPP) preferred</li>
                            <li>Experience working with technology companies</li>
                            <li>Excellent written and verbal communication skills</li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="application-process">
                      <h4>How to Apply</h4>
                      <p>Send your resume and a brief cover letter explaining your interest in fractional compliance services to:</p>
                      <p><strong>Email:</strong> <a href="mailto:careers@soc2experts.com">careers@soc2experts.com</a></p>
                      <p><strong>Subject Line:</strong> [Position Name] - [Your Name]</p>
                      
                      <div className="application-note">
                        <h5>What We're Looking For</h5>
                        <ul>
                          <li>Proven expertise in your field with relevant certifications</li>
                          <li>Experience working with growing technology companies</li>
                          <li>Ability to explain complex compliance concepts to non-technical audiences</li>
                          <li>Entrepreneurial mindset and comfort with a growing company</li>
                          <li>Commitment to delivering exceptional client service</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {activeContactTab === 'partnerships' && (
                <>
                  <h3>Partner With Us</h3>
                  <p>Join our partner network to deliver comprehensive SOC 2 services to your clients. We offer multiple partnership models to fit your business.</p>

                  <div className="partnerships-section">
                    <div className="partnership-types">
                      <h4>Partnership Opportunities</h4>
                      
                      <div className="partnership-type">
                        <h5>🤝 Referral Partners</h5>
                        <div className="partnership-details">
                          <p><strong>Best For:</strong> Consultants, accountants, lawyers, and other service providers who encounter clients needing SOC 2 compliance</p>
                          <div className="partnership-benefits">
                            <h6>Benefits:</h6>
                            <ul>
                              <li>25% commission on referred client revenue (first year)</li>
                              <li>10% ongoing commission for active clients</li>
                              <li>Co-branded materials and proposals</li>
                              <li>Partner portal with tracking and reporting</li>
                              <li>No exclusivity requirements</li>
                            </ul>
                          </div>
                          <div className="partnership-requirements">
                            <h6>Requirements:</h6>
                            <ul>
                              <li>Professional services background</li>
                              <li>Understanding of compliance or security concepts</li>
                              <li>Existing client relationships that could benefit from SOC 2</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="partnership-type">
                        <h5>🏢 Reseller Partners</h5>
                        <div className="partnership-details">
                          <p><strong>Best For:</strong> Consulting firms, accounting practices, and managed service providers who want to offer SOC 2 services under their brand</p>
                          <div className="partnership-benefits">
                            <h6>Benefits:</h6>
                            <ul>
                              <li>White-label access to our platform and services</li>
                              <li>30-40% margin on client engagements</li>
                              <li>Comprehensive training and certification program</li>
                              <li>Marketing and sales support materials</li>
                              <li>Technical support and expert consultation</li>
                            </ul>
                          </div>
                          <div className="partnership-requirements">
                            <h6>Requirements:</h6>
                            <ul>
                              <li>Established professional services firm</li>
                              <li>Minimum 5 potential clients for SOC 2 services</li>
                              <li>Dedicated team member for SOC 2 services</li>
                              <li>Commitment to completing our certification program</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="partnership-type">
                        <h5>🔧 Technology Partners</h5>
                        <div className="partnership-details">
                          <p><strong>Best For:</strong> Software vendors, cloud providers, and security tool companies whose customers need SOC 2 compliance</p>
                          <div className="partnership-benefits">
                            <h6>Benefits:</h6>
                            <ul>
                              <li>Joint go-to-market opportunities</li>
                              <li>Integration possibilities with our platform</li>
                              <li>Co-marketing and thought leadership opportunities</li>
                              <li>Revenue sharing on mutual clients</li>
                              <li>Enhanced value proposition for your customers</li>
                            </ul>
                          </div>
                          <div className="partnership-requirements">
                            <h6>Requirements:</h6>
                            <ul>
                              <li>Established technology platform with SOC 2-relevant customers</li>
                              <li>Customer base that would benefit from compliance services</li>
                              <li>Technical team available for potential integrations</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="partnership-type">
                        <h5>🎓 Training Partners</h5>
                        <div className="partnership-details">
                          <p><strong>Best For:</strong> Training organizations, educational institutions, and certification bodies</p>
                          <div className="partnership-benefits">
                            <h6>Benefits:</h6>
                            <ul>
                              <li>Curriculum development collaboration</li>
                              <li>Guest lecture and workshop opportunities</li>
                              <li>Real-world case studies and examples</li>
                              <li>Career pathway for your graduates</li>
                              <li>Industry expertise and insights</li>
                            </ul>
                          </div>
                          <div className="partnership-requirements">
                            <h6>Requirements:</h6>
                            <ul>
                              <li>Established training or educational programs</li>
                              <li>Focus on security, compliance, or related fields</li>
                              <li>Commitment to high-quality educational content</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="partnership-process">
                      <h4>Partnership Process</h4>
                      <div className="process-steps">
                        <div className="process-step">
                          <h5>1. Initial Discussion</h5>
                          <p>We discuss your business, client base, and partnership interests to determine the best fit.</p>
                        </div>
                        <div className="process-step">
                          <h5>2. Partnership Agreement</h5>
                          <p>Formalize the partnership with clear terms, expectations, and compensation structure.</p>
                        </div>
                        <div className="process-step">
                          <h5>3. Training & Onboarding</h5>
                          <p>Comprehensive training on our services, platform, and best practices for SOC 2 consulting.</p>
                        </div>
                        <div className="process-step">
                          <h5>4. Launch & Support</h5>
                          <p>Begin serving clients with ongoing support, resources, and partnership development.</p>
                        </div>
                      </div>
                    </div>

                    <div className="partnership-contact">
                      <h4>Start a Partnership</h4>
                      <p>Ready to explore partnership opportunities? We'd love to discuss how we can work together to serve your clients' SOC 2 needs.</p>
                      
                      <div className="contact-grid">
                        <div className="contact-method">
                          <h5>Partnership Inquiry</h5>
                          <p>Email us with details about your business and partnership interests.</p>
                          <a href="mailto:partnerships@soc2experts.com" className="contact-btn">partnerships@soc2experts.com</a>
                        </div>

                        <div className="contact-method">
                          <h5>Schedule a Call</h5>
                          <p>Book a call to discuss partnership opportunities in detail.</p>
                          <button className="contact-btn" onClick={() => setShowConsultationForm(true)}>Schedule Partnership Call</button>
                        </div>
                      </div>

                      <div className="partnership-note">
                        <p><strong>Note:</strong> All partnerships are subject to qualification and mutual agreement. We're selective about our partners to ensure quality service delivery and strong client outcomes.</p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {showLogin && (
        <div className="modal-overlay" onClick={() => setShowLogin(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowLogin(false)}>×</button>
            <LoginForm onLogin={handleLogin} />
          </div>
        </div>
      )}

      {showConsultationForm && (
        <div className="modal-overlay" onClick={() => setShowConsultationForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowConsultationForm(false)}>×</button>
            <ConsultationForm onClose={() => setShowConsultationForm(false)} />
          </div>
        </div>
      )}

      {showFeatureComparison && (
        <div className="modal-overlay" onClick={() => setShowFeatureComparison(false)}>
          <div 
            className="modal-content feature-comparison-modal" 
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (e.target.closest('.modal-header')) {
                const modal = e.currentTarget;
                const rect = modal.getBoundingClientRect();
                const offsetX = e.clientX - rect.left;
                const offsetY = e.clientY - rect.top;

                const handleMouseMove = (e) => {
                  modal.style.position = 'fixed';
                  modal.style.left = `${e.clientX - offsetX}px`;
                  modal.style.top = `${e.clientY - offsetY}px`;
                  modal.style.transform = 'none';
                };

                const handleMouseUp = () => {
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };

                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }
            }}
          >
            <div className="modal-header">
              <h3>Detailed Feature Comparison</h3>
            </div>
            <button className="modal-close" onClick={() => setShowFeatureComparison(false)}>×</button>
            <div className="feature-comparison-content">
              <div className="billing-toggle">
                <button 
                  className={`billing-option ${activePricingBilling === 'monthly' ? 'active' : ''}`}
                  onClick={() => setActivePricingBilling('monthly')}
                >
                  Monthly Plans
                </button>
                <button 
                  className={`billing-option ${activePricingBilling === 'yearly' ? 'active' : ''}`}
                  onClick={() => setActivePricingBilling('yearly')}
                >
                  Annual Plans
                  <span className="savings-badge">Save up to 50%</span>
                </button>
              </div>

              <div className="comparison-table">
                {activePricingBilling === 'monthly' ? (
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>DIY Toolkit</th>
                        <th>Guided Essentials</th>
                        <th>Co-Pilot Plus</th>
                        <th>Fractional CISO Elite</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Monthly Price</td>
                        <td>$199</td>
                        <td>$499</td>
                        <td>$999</td>
                        <td>$2,500</td>
                      </tr>
                      <tr>
                        <td>Team Members Included</td>
                        <td>10</td>
                        <td>50</td>
                        <td>100</td>
                        <td>200</td>
                      </tr>
                      <tr>
                        <td>Expert Consultations</td>
                        <td>12 per year (1/month)</td>
                        <td>24 per year (2/month)</td>
                        <td>24 per year (2/month)</td>
                        <td>24 per year (2/month)</td>
                      </tr>
                      <tr>
                        <td>SOC 2 Readiness Assessment</td>
                        <td>Self-serve</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Gap Analysis</td>
                        <td>Quarterly report</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Policy Templates</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>Custom Development</td>
                      </tr>
                      <tr>
                        <td>Progress Tracking</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Team Collaboration</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Dedicated Account Manager</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Success Guarantee</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>SOC 2 Inventory</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Training Programs</td>
                        <td>Basic</td>
                        <td>Comprehensive</td>
                        <td>Advanced</td>
                        <td>Executive Level</td>
                      </tr>
                      <tr>
                        <td>Risk Exercises</td>
                        <td>Standard</td>
                        <td>Enhanced</td>
                        <td>Advanced</td>
                        <td>Enterprise</td>
                      </tr>
                      <tr>
                        <td>Virtual CISO</td>
                        <td>❌</td>
                        <td>Basic</td>
                        <td>Standard</td>
                        <td>Executive Level</td>
                      </tr>
                      <tr>
                        <td>Automated Evidence Collection</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Quarterly Board Reporting</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Support Level</td>
                        <td>Slack/Email</td>
                        <td>Priority Support</td>
                        <td>Weekly Check-ins</td>
                        <td>Dedicated CISO</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <table className="comparison-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Starter Annual</th>
                        <th>Professional Annual</th>
                        <th>Enterprise Annual</th>
                        <th>Premium Plus Annual</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Annual Price</td>
                        <td>$2,500</td>
                        <td>$5,000</td>
                        <td>$12,000</td>
                        <td>$25,000</td>
                      </tr>
                      <tr>
                        <td>Maximum System Users</td>
                        <td>20</td>
                        <td>100</td>
                        <td>200</td>
                        <td>400</td>
                      </tr>
                      <tr>
                        <td>Expert Consultations</td>
                        <td>12 sessions (1/month)</td>
                        <td>24 sessions (2/month)</td>
                        <td>24 sessions (2/month)</td>
                        <td>24 sessions (2/month)</td>
                      </tr>
                      <tr>
                        <td>SOC 2 Readiness Assessment</td>
                        <td>Complete</td>
                        <td>Complete</td>
                        <td>Complete</td>
                        <td>Complete</td>
                      </tr>
                      <tr>
                        <td>Gap Analysis</td>
                        <td>Comprehensive</td>
                        <td>Comprehensive</td>
                        <td>Comprehensive</td>
                        <td>Comprehensive</td>
                      </tr>
                      <tr>
                        <td>Policy Templates</td>
                        <td>✓</td>
                        <td>Complete</td>
                        <td>Custom Development</td>
                        <td>Custom Development</td>
                      </tr>
                      <tr>
                        <td>Progress Tracking</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Team Collaboration</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Dedicated Account Manager</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Success Guarantee</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>SOC 2 Inventory</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                        <td>✓</td>
                      </tr>
                      <tr>
                        <td>Training Programs</td>
                        <td>Basic</td>
                        <td>Comprehensive</td>
                        <td>Advanced</td>
                        <td>Executive Level</td>
                      </tr>
                      <tr>
                        <td>Risk Exercises</td>
                        <td>Standard</td>
                        <td>Enhanced</td>
                        <td>Advanced</td>
                        <td>Enterprise</td>
                      </tr>
                      <tr>
                        <td>Virtual CISO</td>
                        <td>Basic</td>
                        <td>Enhanced</td>
                        <td>Executive Level</td>
                        <td>Board Level</td>
                      </tr>
                      <tr>
                        <td>Support Level</td>
                        <td>Email</td>
                        <td>Priority Email & Phone</td>
                        <td>24/7 Priority</td>
                        <td>Dedicated Success Team</td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Consultation Button - only show when not logged in */}
      {!user && (
        <div className="floating-consultation-btn" onClick={() => setShowConsultationForm(true)}>
          <span className="consultation-icon">📞</span>
          <span className="consultation-text">Schedule Consultation</span>
        </div>
      )}

      <footer className="app-footer">
        <div className="footer-content">
          <p>&copy; 2025 LocalPro, Inc. | soc2experts.com is a project of LocalPro, Inc.</p>
          <p>Phone: (415) 906-6005 | Email: info@soc2experts.com</p>
        </div>
      </footer>
    </div>
  );
};

export default App;